import React, {useCallback, useMemo, useRef, useState} from 'react';
import maCateImg from "../../assets/img/m-a-cate.png";
import '../../css/web/AdminPageSetPg.css';
import '../../css/web/AdminRenewTaxPg.css';
import '../../css/DarkMode.css';
import axios from '../../services/axiosInstance';
import taxExcel from '../../assets/motion/taxExcel.json';
import Lottie from "lottie-react";
import AdminYmdEvent from "../../components/web/layoutsadmin/AdminYmdEvent";
import AdminRenewTaxFilterModal from "../../components/web/modal/AdminRenewTaxFilterModal";
import * as XLSX from "xlsx";
import {saveAs} from 'file-saver';
import * as ExcelJS from "exceljs";

/* =============================================================================
 * AdminTaxPg 리뉴얼 컴포넌트
 * - [청구서] / [부가세] / [법인세] 탭으로 분류
 * - 청구서탭 : EMS / FedEx / UPS 청구서 엑셀을 업로드하여
 *              DB(ShipInfo - Orderdt - PayInfo)와 운송장번호 기준으로 대조/집계
 * ============================================================================= */

/**
 * ⚠ [임시] 운영 데이터 검증용 운영서버 주소
 * axiosInstance 의 baseURL 은 개발서버(https://btorage-dev.com/api)를 바라보지만
 * 청구서/세무 집계는 운영 데이터로 검증해야 하므로 각 배송사별 조회함수를 2종으로 작성한다.
 *   1) baseURL 의존 함수      : selectedXxxShipBillData()
 *   2) 운영서버 full 경로 함수 : selectedXxxShipBillDataForProd()
 * 운영 데이터 검증이 끝나면 [운영] 토글과 ForProd 함수를 제거하고 1)만 사용한다.
 */
const PROD_API_BASE_URL = 'https://btorage.com/api';

/* region 배송사별 청구서 설정 --------------------------------------------------
 * - 파일명은 언제든 변경될 수 있으므로 파일명 매핑은 하지 않고
 *   "운송장번호 열 + 운송장번호 패턴"만으로 데이터 row 를 판별한다.
 *   (헤더행/합계행/빈행 등은 패턴 불일치로 자동 제외되므로 헤더 행수를 하드코딩하지 않음)
 * -------------------------------------------------------------------------- */
const BILL_CARRIERS = {
    EMS: {
        key: 'EMS',
        label: 'EMS',
        fullLabel: 'EMS 소포수령증',
        waybillCol: 'D',            // 등기번호(운송장번호)
        altWaybillCol: '',
        amountCol: 'I',             // 총요금(청구운송료)
        typeCol: '',                // 유형 구분열 없음
        typeColLabel: '구분',
        rowFilter: null,            // 행 필터 없음(모든 운송장 행 집계)
        // 실제 소포수령증 D열은 'EG049413223KR' 형태(영문2+숫자9+국가2)이며,
        // 향후 숫자 12~13자리로 변경될 가능성까지 함께 수용한다.
        waybillPattern: /^(?:[A-Z]{2}\d{9}[A-Z]{2}|\d{12,13})$/i,
        waybillPatternDesc: '영문2+숫자9+국가2(예:EG049413223KR) 또는 숫자 12~13자리',
        // 집계목록오류의 주문번호는 T열(고객주문번호 = 비토리지 주문번호)을 사용한다.
        // B열(예약번호)은 우체국이 발급하는 접수 예약번호라 주문번호가 아니므로 참고컬럼으로만 노출한다.
        orderNoCol: 'T',            // 고객주문번호(비토리지 주문번호)
        orderNoPrefix: '26',
        refOrderNoCol: 'B',         // 참고 : 우체국 예약번호
        refOrderNoLabel: '예약번호(B열)',
        dvTypes: ['D02', 'D15', 'D13'],
        themeColor: '#E4572E',
    },
    FEDEX: {
        key: 'FEDEX',
        label: 'FedEx',
        fullLabel: 'FedEx 청구서',
        waybillCol: 'Q',            // 항공운송장 번호
        altWaybillCol: '',
        amountCol: 'BN',            // 항공운송장 총 금액
        typeCol: 'B',               // 청구서 유형(운송 / 관세·세금)
        typeColLabel: '청구서 유형',
        // ✅ 실청구액은 B열이 '운송'인 row 만 집계한다(관세/세금 행은 제외)
        rowFilter: (typeValue) => String(typeValue || '').includes('운송'),
        rowFilterDesc: 'B열 = "운송" 인 행만 집계 (관세/세금 행 제외)',
        waybillPattern: /^\d{12}$/,
        waybillPatternDesc: '숫자 12자리',
        orderNoCol: 'S',            // 발송인 참조1 = 비토리지 주문번호
        orderNoPrefix: '26',
        refOrderNoCol: '',
        refOrderNoLabel: '',
        dvTypes: ['D01', 'D16'],
        themeColor: '#4B89DC',
    },
    UPS: {
        key: 'UPS',
        label: 'UPS',
        fullLabel: 'UPS 청구서',
        waybillCol: 'N',            // 운송장번호
        altWaybillCol: 'U',         // N열이 비었을 때 대체 운송장번호 열
        amountCol: 'BA',            // 해외배송비(청구운송료)
        typeCol: 'AR',              // 요금유형(FRT/FSC/ACC/TAX/INF)
        typeColLabel: '요금유형',
        rowFilter: null,
        waybillPattern: /^1Z[A-Z0-9]{6,}$/i,
        waybillPatternDesc: '1Z 로 시작',
        orderNoCol: 'P',
        orderNoPrefix: '',          // UPS 는 '26' 시작 조건 없음
        refOrderNoCol: '',
        refOrderNoLabel: '',
        dvTypes: ['D11', 'D17'],
        themeColor: '#8B6F4E',
    },
};
const CARRIER_ORDER = ['EMS', 'FEDEX', 'UPS'];
/* endregion */

/* region 공통 유틸 ----------------------------------------------------------- */

/**
 * 엑셀 셀값 → 숫자 변환
 * - "28,920.00"(문자), 28920(숫자), "₩ 1,000", "(1,000)"(회계식 음수) 를 모두 안전하게 처리
 */
const toNumber = (value) => {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    let text = String(value).trim();
    if (!text) return 0;

    let isNegative = false;
    if (/^\(.*\)$/.test(text)) {   // 회계표기 음수 : (1,000) → -1000
        isNegative = true;
        text = text.slice(1, -1);
    }
    text = text.replace(/[^0-9.-]/g, '');   // 통화기호/쉼표/공백/문자 제거
    const num = parseFloat(text);
    if (!Number.isFinite(num)) return 0;
    return isNegative ? -Math.abs(num) : num;
};

/**
 * 숨김(hidden) 처리된 행인지 판별
 * - 관리자가 엑셀에서 숨김처리한 행은 화면에서 드래그/[Shift+Ctrl+↓] 로 잡히지 않으므로 집계대상에서 제외한다.
 * - 숨김정보(worksheet['!rows'])는 XLSX.read 시 { cellStyles: true } 옵션이 있어야만 채워진다.
 */
const isHiddenRow = (worksheet, rowNum) => {
    const rowsMeta = worksheet['!rows'];
    if (!rowsMeta) return false;
    const meta = rowsMeta[rowNum - 1];
    return !!(meta && meta.hidden);
};

// 엑셀 읽기 공통옵션 (cellStyles 를 켜야 숨김행 정보를 얻을 수 있음)
const XLSX_READ_OPTIONS = {type: 'array', cellStyles: true};

// 셀 원문 문자열 추출
const cellText = (worksheet, address) => {
    if (!address) return '';
    const cell = worksheet[address];
    if (!cell || cell.v === undefined || cell.v === null) return '';
    return String(cell.v).trim();
};

const formatWon = (num) => `${Math.round(Number(num) || 0).toLocaleString()}원`;
const formatNum = (num) => `${Math.round(Number(num) || 0).toLocaleString()}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// 배송사코드(dvType) → 사용자 용어 변환 (AdminTaxPg.convertToShipco 동일규칙)
const convertToShipco = (dvType, defaultShipco) => {
    if (dvType === 'D00') return '국내배송';
    if (dvType === 'D01') return 'FedEx';
    if (dvType === 'D16') return 'FedEx-E';
    if (dvType === 'D11') return 'UPS';
    if (dvType === 'D17') return 'UPS-E';
    if (dvType === 'D02') return 'EMS';
    if (dvType === 'D15') return 'EMSP';
    if (dvType === 'D04') return 'DHL';
    if (dvType === 'D13') return 'K-Packet';
    if (dvType === 'D14') return 'Small-Packet';
    if (dvType === 'D03') return 'SEA';
    return defaultShipco || '';
};

// 동일 운송장에 복수 PayInfo 가 매칭될 때 출고완료(PS3) 를 우선채택
const payStatusPriority = (payStatus) => (payStatus === 'PS3' ? 0 : payStatus === 'PS2' ? 1 : 2);

/* region 부가세탭 공통 상수/유틸 ------------------------------------------------ */

// 상품유형 코드 (P00:장바구니 / P01:구매대행 / P02:오프라인 / P03:이체대행 / P04:국내배송 / P05:배송대행)
const PROD_TY_OFFLINE = 'P02';
const PROD_TY_DOMESTIC = 'P04';
const PROD_TY_DELIVERY = 'P05';

// yyyy-MM-dd 포맷
const formatDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// 분기(1~4) → 해당 연도의 시작일/종료일
const getQuarterRange = (year, quarter) => {
    const startMonth = (quarter - 1) * 3;          // 0,3,6,9
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0); // 다음 분기 시작 -1일 = 해당 분기 말일
    return {start: formatDateString(start), end: formatDateString(end)};
};

// 주문서에 오프라인폼(P02) 또는 국내배송폼(P04) 상품이 하나라도 있는지
const hasOfflineOrDomesticForm = (item) =>
    (item.prodTypes || []).some((ty) => ty === PROD_TY_OFFLINE || ty === PROD_TY_DOMESTIC);

// 주문서에 오직 배송대행폼(P05) 상품만 있는지
const hasOnlyDeliveryForm = (item) => {
    const types = item.prodTypes || [];
    return types.length > 0 && types.every((ty) => ty === PROD_TY_DELIVERY);
};

/* ── 부가세탭 업로드폼 정의 ──────────────────────────────────────────────────
 * 파일명은 언제든 변경될 수 있으므로 파일명 매핑은 하지 않는다.
 * carrierKey 가 있는 항목은 BILL_CARRIERS 설정을 함께 사용해 청구오류폼(운송장 미매칭)까지 산출한다.
 * ------------------------------------------------------------------------ */
const VAT_UPLOAD_FORMS = [
    // passThrough : 최종 세무제출 파일에 업로드 원본 그대로(서식/병합/숨김행 포함) 삽입되는 항목
    {key: 'EMS_BILL', label: 'EMS 수령증', amountCol: 'I', carrierKey: 'EMS', passThrough: true},
    {key: 'FEDEX_BILL', label: 'FedEx 청구서', amountCol: 'BN', carrierKey: 'FEDEX', passThrough: true},
    {key: 'UPS_BILL', label: 'UPS 청구서', amountCol: 'BA', carrierKey: 'UPS', passThrough: true},
    {key: 'ORDER_CARD', label: '구매카드내역', amountCol: 'P', amountColDesc: 'P(병합 P~S)', passThrough: true},
    {key: 'OVERSEA_CARD', label: '해외카드내역', amountCol: 'P', amountColDesc: 'P(병합 P~S)', passThrough: true},
    {key: 'COST_CARD', label: '비용카드내역', amountCol: 'P', amountColDesc: 'P(병합 P~S)', postPayback: true},
    {key: 'ORDER_LOG', label: '구매일지', purchaseLog: true},
];

// 비용카드내역에서 우체국 페이백을 추출할 가맹점명(병합 M~O열)
const POST_OFFICE_NAMES = ['우정사업본부(우체국)', '우정사업본부', '우체국'];

// 구매일지 시트명("2026년 6월")에서 연/월 추출
const parseLogSheetYearMonth = (sheetName) => {
    const matched = String(sheetName || '').match(/(\d{4})\s*년\s*(\d{1,2})\s*월/);
    if (!matched) return null;
    return {year: Number(matched[1]), month: Number(matched[2])};
};

// yyyy-MM-dd → yyyyMM 숫자
const toYearMonthNumber = (dateText) => {
    if (!dateText) return null;
    const [y, m] = dateText.split('-');
    if (!y || !m) return null;
    return Number(y) * 100 + Number(m);
};

/**
 * 전체취소 여부 판정 (구매일지 E~G열 비고)
 *
 * 요구사항 : "'부분취소'는 C열 값이 그대로 합산에 포함되지만,
 *            '전체취소' 혹은 앞에 아무런 문구없이 '취소'/'주문후 품절' 등 전체취소는 합산 제외"
 *
 * 판정순서 (공백 무시)
 *  1) '부분취소' 문구 제거          → 예) "27,880원부분취소"          ⇒ 부분취소이므로 포함
 *  2) "<금액>원 취소/품절" 제거     → 예) "209,000원품절"             ⇒ 금액이 명시되면 부분취소이므로 포함
 *  3) 남은 문구에 '취소'/'품절' 존재 → 예) "주문후취소","선구매후취소","주문후품절" ⇒ 전체취소이므로 제외
 */
const isFullCancelText = (text) => {
    const normalized = String(text || '').replace(/\s/g, '');
    if (!normalized) return false;
    let remain = normalized.split('부분취소').join('');
    // 금액이 앞에 명시된 취소/품절은 부분취소로 간주
    remain = remain.replace(/[0-9][0-9,]*원(취소|품절)/g, '');
    return remain.includes('취소') || remain.includes('품절');
};

// 어떤 형태로든 '취소' 문구가 있는지 (이체대행 기준)
const hasAnyCancelText = (text) => String(text || '').replace(/\s/g, '').includes('취소');

// 5가지 각종 수수료 합계 (배송대행/재포장/대행/압축팩/제거작업)
const sumFiveFees = (items) => items.reduce((acc, item) => ({
    deliveryFee: acc.deliveryFee + (item.deliveryFee || 0),
    repackFee: acc.repackFee + (item.repackFee || 0),
    agencyFee: acc.agencyFee + (item.agencyFee || 0),
    comprFee: acc.comprFee + (item.comprFee || 0),
    removeFee: acc.removeFee + (item.removeFee || 0),
    total: acc.total + (item.deliveryFee || 0) + (item.repackFee || 0) + (item.agencyFee || 0)
        + (item.comprFee || 0) + (item.removeFee || 0),
}), {deliveryFee: 0, repackFee: 0, agencyFee: 0, comprFee: 0, removeFee: 0, total: 0});

/* endregion */

const createInitialBillState = () => ({
    fileName: '',
    sheetName: '',
    isProcessing: false,
    isDone: false,
    progress: 0,
    progressLabel: '',
    errorMsg: '',
    rows: [],        // 집계목록 (DB 매칭 성공)
    errorRows: [],   // 집계목록오류 (DB 미매칭)
    summary: null,
});

/* endregion */

function AdminRenewTaxPg() {

    /* region 탭 상태 --------------------------------------------------------- */
    const [mainTab, setMainTab] = useState('BILL');              // BILL(청구서) / VAT(부가세) / CT(법인세)
    const [billCarrierTab, setBillCarrierTab] = useState('EMS'); // EMS / FEDEX / UPS
    /* endregion */

    /* region 청구서탭 상태 (배송사별 완전분리) --------------------------------- */
    const [billData, setBillData] = useState({
        EMS: createInitialBillState(),
        FEDEX: createInitialBillState(),
        UPS: createInitialBillState(),
    });
    const [draggingKey, setDraggingKey] = useState('');
    const [expandedRowKey, setExpandedRowKey] = useState('');
    const [billDataSource, setBillDataSource] = useState('DEV');  // DEV(개발 baseURL) / PROD(운영 full 경로)

    const emsInputRef = useRef(null);
    const fedexInputRef = useRef(null);
    const upsInputRef = useRef(null);
    const getFileInputRef = (carrierKey) =>
        carrierKey === 'EMS' ? emsInputRef : carrierKey === 'FEDEX' ? fedexInputRef : upsInputRef;

    const patchBill = useCallback((carrierKey, patch) => {
        setBillData((prev) => ({...prev, [carrierKey]: {...prev[carrierKey], ...patch}}));
    }, []);
    /* endregion */

    /* region 부가세탭 상태 (청구서탭과 날짜/상태값 완전분리) ---------------------- */
    const [vatStartDate, setVatStartDate] = useState('');
    const [vatEndDate, setVatEndDate] = useState('');
    const [vatQuarter, setVatQuarter] = useState(0);          // 1~4 (분기버튼 활성표시용, 수동변경 시 0)
    const [vatLoading, setVatLoading] = useState(false);
    const [vatErrorMsg, setVatErrorMsg] = useState('');

    const [vatPt1All, setVatPt1All] = useState([]);           // 조회된 PT1(해외배송료) 전체
    const [vatPt2All, setVatPt2All] = useState([]);           // 조회된 PT2(기타비용) 전체
    const [vatFilterShow, setVatFilterShow] = useState(false);// 세무 데이터 필터링 팝업
    const [vatPt1Filtered, setVatPt1Filtered] = useState([]); // 필터완료(체크선택)된 PT1 = 실제 집계대상
    const [vatFilterDone, setVatFilterDone] = useState(false);
    const [vatAgg, setVatAgg] = useState(null);               // 사항3 집계결과
    const [vatUsePointList, setVatUsePointList] = useState([]); // 적립금 사용/취소 내역

    // 부모 컴포넌트 필터목록 UI용 배송사탭/검색 필터
    const [vatListShipcoTab, setVatListShipcoTab] = useState('ALL');
    const [vatListSearch, setVatListSearch] = useState('');

    const vatStartHandle = (e) => { setVatStartDate(e.target.value); setVatQuarter(0); };
    const vatEndHandle = (e) => { setVatEndDate(e.target.value); setVatQuarter(0); };

    // 분기버튼 : 최신년도 기준으로 시작일~종료일 세팅 (이후 날짜 직접수정도 자유롭게 가능)
    const handleQuarterClick = (quarter) => {
        const {start, end} = getQuarterRange(new Date().getFullYear(), quarter);
        setVatStartDate(start);
        setVatEndDate(end);
        setVatQuarter(quarter);
    };
    /* endregion */

    /* region 부가세탭 DB 조회함수 (2종 : 개발 baseURL / 운영 full 경로) ----------- */

    // [부가세 / 개발] axiosInstance 의 baseURL(https://btorage-dev.com/api) 사용
    const selectedRenewTaxData = async (startDate, endDate) => {
        const response = await axios.get('/order/selectedRenewTaxData', {params: {startDate, endDate}});
        return response.data || [];
    };
    // [부가세 / 운영] ⚠ 임시 - 운영서버 full 경로 직접조회 (운영데이터 검증완료 후 제거예정)
    const selectedRenewTaxDataForProd = async (startDate, endDate) => {
        const response = await axios.get(`${PROD_API_BASE_URL}/order/selectedRenewTaxData`, {params: {startDate, endDate}});
        return response.data || [];
    };

    // [적립금 사용/취소내역 / 개발]
    const selectedRenewTaxUsePoint = async (startDate, endDate) => {
        const response = await axios.get('/member/selectedRenewTaxUsePoint', {params: {startDate, endDate}});
        return response.data || [];
    };
    // [적립금 사용/취소내역 / 운영] ⚠ 임시
    const selectedRenewTaxUsePointForProd = async (startDate, endDate) => {
        const response = await axios.get(`${PROD_API_BASE_URL}/member/selectedRenewTaxUsePoint`, {params: {startDate, endDate}});
        return response.data || [];
    };
    /* endregion */

    /* region 부가세탭 데이터조회 --------------------------------------------------- */
    const handleSelectVatData = async () => {
        if (!vatStartDate || !vatEndDate) {
            setVatErrorMsg('조회 시작일과 종료일을 먼저 설정해주세요.');
            return;
        }
        if (vatStartDate > vatEndDate) {
            setVatErrorMsg('시작일이 종료일보다 늦을 수 없습니다.');
            return;
        }

        setVatLoading(true);
        setVatErrorMsg('');
        setVatFilterDone(false);
        setVatPt1Filtered([]);
        setVatAgg(null);

        // 백엔드 조회조건은 yyyyMMdd 문자열 기준
        const formattedStart = vatStartDate.replace(/-/g, '');
        const formattedEnd = vatEndDate.replace(/-/g, '');

        try {
            const isProd = billDataSource === 'PROD';
            const fetchTaxData = isProd ? selectedRenewTaxDataForProd : selectedRenewTaxData;
            const fetchUsePoint = isProd ? selectedRenewTaxUsePointForProd : selectedRenewTaxUsePoint;

            const [taxData, usePoint] = await Promise.all([
                fetchTaxData(formattedStart, formattedEnd),
                fetchUsePoint(formattedStart, formattedEnd),
            ]);

            const pt1Items = (taxData || []).filter((item) => item.payType === 'PT1')
                .sort((a, b) => (b.paySeqNum || 0) - (a.paySeqNum || 0));
            const pt2Items = (taxData || []).filter((item) => item.payType === 'PT2')
                .sort((a, b) => (b.paySeqNum || 0) - (a.paySeqNum || 0));

            setVatPt1All(pt1Items);
            setVatPt2All(pt2Items);
            setVatUsePointList((usePoint || []).sort((a, b) => (b.seqNum || 0) - (a.seqNum || 0)));

            if (pt1Items.length > 0) {
                setVatFilterShow(true);   // 세무 데이터 필터링 팝업 오픈
            } else {
                setVatErrorMsg('해당 기간에 조회된 출고완료(PT1) 데이터가 없습니다.');
            }
        } catch (error) {
            console.error('[부가세] 세무 데이터조회 오류 : ', error);
            setVatErrorMsg(error?.response?.status
                ? `세무 데이터조회에 실패했습니다. (HTTP ${error.response.status}) 백엔드 배포상태를 확인해주세요.`
                : (error?.message || '세무 데이터조회 중 오류가 발생했습니다.'));
        } finally {
            setVatLoading(false);
        }
    };

    /**
     * [사항3] 필터완료된 PT1 + 전체 PT2 를 기반으로 세무 집계값을 산출한다.
     * - 필터에서 제외(체크해제)된 PT1 데이터는 이후 모든 세무/비용 계산에 사용하지 않는다.
     */
    const buildVatAggregation = (pt1Items, pt2Items) => {
        const sumBy = (items, field) => items.reduce((sum, item) => sum + (item[field] || 0), 0);

        /* ── 배송사별 배송료 합계 (기존 selectedTaxData 와 동일 규칙) ── */
        const emsShipSum = sumBy(pt1Items.filter((i) => ['D02', 'D13', 'D15'].includes(i.dvType)), 'ttShipExpense');
        const emsEtcSum = sumBy(pt2Items.filter((i) =>
            i.etcPriceNm?.includes('Add missing Post-Office') || i.etcPriceNm?.includes('Add missing K-Packet')), 'ttEtcPrice');

        const fedexShipSum = sumBy(pt1Items.filter((i) => ['D01', 'D16'].includes(i.dvType)), 'ttShipExpense');
        const fedexEtcSum = sumBy(pt2Items.filter((i) => i.etcPriceNm?.includes('Add missing FedEx')), 'ttEtcPrice');

        const upsShipSum = sumBy(pt1Items.filter((i) => ['D11', 'D17'].includes(i.dvType)), 'ttShipExpense');
        const upsEtcSum = sumBy(pt2Items.filter((i) => i.etcPriceNm?.includes('Add missing UPS')), 'ttEtcPrice');

        /* ── 선편/K-Packet/S-Packet 고객총결제 (수동입력폼 자동계산용) ──
         * convertToShipco 기준 : D03=SEA(선편) / D13=K-Packet / D14=Small-Packet(S-Packet)
         * ※ D13(K-Packet) 매출은 emsShipSum 에도 포함되어 있으나, emsShipSum 은 화면 참고용일 뿐
         *    최종 세무제출 파일(탭1 F11~F16) 어디에도 사용되지 않으므로 중복 계상되지 않는다. */
        const seaCustomerSum = sumBy(pt1Items.filter((i) => i.dvType === 'D03'), 'ttShipExpense');
        const kPackCustomerSum = sumBy(pt1Items.filter((i) => i.dvType === 'D13'), 'ttShipExpense');
        const sPackCustomerSum = sumBy(pt1Items.filter((i) => i.dvType === 'D14'), 'ttShipExpense');

        /* ── 국내배송(D00) 택배비 수익 : 폼 종류별 분류 ── */
        const isDomesticShip = (i) => i.shipco === '국내배송' && i.dvType === 'D00';
        const domesticFormItems = pt1Items.filter((i) => isDomesticShip(i) && hasOfflineOrDomesticForm(i));
        const deliveryFormItems = pt1Items.filter((i) => isDomesticShip(i) && hasOnlyDeliveryForm(i));
        const profitOf = (items) => items.reduce((sum, i) => sum + ((i.ttShipExpense || 0) - (i.shipPrice || 0)), 0);

        const domesticFormShipFee = profitOf(domesticFormItems);   // 오프라인+국내배송폼 (택배비 - 송료)
        const deliveryFormShipFee = profitOf(deliveryFormItems);   // 배송대행폼 (택배비 - 송료)

        /* ── 각종 수수료 3분류 (재료 : pt2Items / etcFee 제외, removeFee 포함) ── */
        const overseasPt2 = pt2Items.filter((i) => i.shipco !== '국내배송');
        const domesticFormPt2 = pt2Items.filter((i) => i.shipco === '국내배송' && hasOfflineOrDomesticForm(i));
        const deliveryFormPt2 = pt2Items.filter((i) => i.shipco === '국내배송' && hasOnlyDeliveryForm(i));

        const overseasFee = sumFiveFees(overseasPt2);
        const domesticFormFee = sumFiveFees(domesticFormPt2);
        const deliveryFormFee = sumFiveFees(deliveryFormPt2);

        return {
            // 원재료 건수
            pt1Count: pt1Items.length,
            pt2Count: pt2Items.length,

            // 배송사별
            emsShipSum, emsEtcSum, ttEmsPrice: emsShipSum + emsEtcSum,
            fedexShipSum, fedexEtcSum, ttFedexPrice: fedexShipSum + fedexEtcSum,
            upsShipSum, upsEtcSum, ttUpsPrice: upsShipSum + upsEtcSum,
            seaCustomerSum, kPackCustomerSum, sPackCustomerSum,

            // 국내배송 택배비 수익
            domesticFormItemCount: domesticFormItems.length,
            deliveryFormItemCount: deliveryFormItems.length,
            domesticFormShipFee,
            deliveryFormShipFee,

            // 수수료 3분류
            overseasPt2Count: overseasPt2.length,
            domesticFormPt2Count: domesticFormPt2.length,
            deliveryFormPt2Count: deliveryFormPt2.length,
            overseasFee,
            domesticFormFee,
            deliveryFormFee,

            // 최종값 (탭1 F12 / F14 / F15 에 사용)
            overseasFeeTotal: overseasFee.total,
            domesticFormFeeTotal: domesticFormShipFee + domesticFormFee.total,
            deliveryFormFeeTotal: deliveryFormShipFee + deliveryFormFee.total,

            aggregatedAt: new Date().toLocaleString(),
        };
    };

    // 필터완료 → 부모 컴포넌트에 집계대상 데이터 삽입 + 사항3 집계 수행
    const handleCompleteVatFilter = (filteredData) => {
        setVatFilterShow(false);
        setVatPt1Filtered(filteredData);
        setVatFilterDone(true);
        setVatListShipcoTab('ALL');
        setVatListSearch('');
        setVatAgg(buildVatAggregation(filteredData, vatPt2All));
        if (!filteredData.length) setVatErrorMsg('선택된 데이터가 없습니다. 다시 조회 후 필터해주세요.');
        else setVatErrorMsg('');
    };

    // 부모 컴포넌트 필터목록 : 배송사탭 + 검색어 적용
    const vatVisibleList = useMemo(() => {
        const keyword = vatListSearch.trim().toLowerCase();
        const numericKeyword = keyword.replace(/[^0-9]/g, '');
        return vatPt1Filtered.filter((item) => {
            if (vatListShipcoTab !== 'ALL' && item.dvType !== vatListShipcoTab) return false;
            if (!keyword) return true;
            const boxNo = `${item.orderNumber || ''}-${item.boxNum || ''}`.toLowerCase();
            return boxNo.includes(keyword)
                || (item.regNm || '').toLowerCase().includes(keyword)
                || convertToShipco(item.dvType, item.shipco).toLowerCase().includes(keyword)
                || (item.outWayNum || '').toLowerCase().includes(keyword)
                || (numericKeyword !== '' && String(item.ttShipExpense || 0).includes(numericKeyword));
        });
    }, [vatPt1Filtered, vatListShipcoTab, vatListSearch]);

    // 필터목록 배송사 탭 구성(실제 존재하는 배송사만)
    const vatListShipcoTabs = useMemo(() => {
        const exists = new Set(vatPt1Filtered.map((i) => i.dvType).filter(Boolean));
        const ordered = ['D01', 'D16', 'D11', 'D17', 'D02', 'D15', 'D13', 'D14', 'D03', 'D00', 'D04']
            .filter((code) => exists.has(code));
        return ['ALL', ...ordered];
    }, [vatPt1Filtered]);
    /* endregion */

    /* region 부가세탭 업로드폼 (사항4) ------------------------------------------- */
    const [vatUploads, setVatUploads] = useState({});          // key → 파싱결과
    const [vatDraggingKey, setVatDraggingKey] = useState('');
    const vatFileInputRef = useRef(null);
    const vatUploadTargetRef = useRef('');

    // 수동입력폼 (선편 / K-Packet / S-Packet 총 청구금액 + Anell 총 수수료)
    const [manualSeaBilled, setManualSeaBilled] = useState('');
    const [manualKPackBilled, setManualKPackBilled] = useState('');
    const [manualSPackBilled, setManualSPackBilled] = useState('');
    const [manualAnellFee, setManualAnellFee] = useState('');

    const patchVatUpload = useCallback((key, patch) => {
        setVatUploads((prev) => ({...prev, [key]: {...(prev[key] || {}), ...patch}}));
    }, []);

    /**
     * 단순 금액열 합계 : 표시(visible)되는 모든 row 의 지정 금액열을 중복 상관없이 전부 합산
     * - 카드내역 파일들은 원본 전체내역에서 필요한 행만 남기고 나머지를 '숨김' 처리해 배포되므로
     *   숨김행을 합산하면 실제 금액과 크게 달라진다. (화면에서 드래그로 잡히는 값만 집계)
     */
    const parseAmountColumnTotal = (worksheet, amountCol) => {
        const ref = worksheet['!ref'];
        if (!ref) throw new Error('엑셀 시트가 비어있습니다.');
        const lastRow = XLSX.utils.decode_range(ref).e.r + 1;
        let total = 0;
        let valuedRowCount = 0;
        let hiddenRowCount = 0;
        let hiddenAmount = 0;
        let firstVisibleRow = 0;
        let lastVisibleRow = 0;
        for (let rowNum = 1; rowNum <= lastRow; rowNum++) {
            const cell = worksheet[`${amountCol}${rowNum}`];
            if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue;
            const amount = toNumber(cell.v);
            if (amount === 0 && !/^-?[\d,.\s]+$/.test(String(cell.v).trim())) continue; // 헤더 등 문자셀 제외
            if (isHiddenRow(worksheet, rowNum)) {   // 숨김행은 집계에서 제외
                hiddenRowCount++;
                hiddenAmount += amount;
                continue;
            }
            if (!firstVisibleRow) firstVisibleRow = rowNum;
            lastVisibleRow = rowNum;
            valuedRowCount++;
            total += amount;
        }
        return {total, valuedRowCount, lastRow, hiddenRowCount, hiddenAmount, firstVisibleRow, lastVisibleRow};
    };

    /**
     * 비용카드내역 → 우체국 페이백 추출 (탭1 D24 / 탭2 재료)
     * 조건 : 가맹점명(병합 M~O)이 우정사업본부 계열 && 금액(병합 P~S)이 음수 && 동일 절대금액이 유일할 것
     */
    const parsePostOfficePayback = (worksheet) => {
        const ref = worksheet['!ref'];
        if (!ref) return {rows: [], total: 0, postRowCount: 0, minusRowCount: 0};
        const range = XLSX.utils.decode_range(ref);
        const lastRow = range.e.r + 1;
        const lastCol = range.e.c;

        // 전체 금액 등장횟수(절대값 기준) 집계 → '유일한 금액' 판별용
        const absCount = new Map();
        for (let rowNum = 1; rowNum <= lastRow; rowNum++) {
            if (isHiddenRow(worksheet, rowNum)) continue;   // 숨김행 제외
            const cell = worksheet[`P${rowNum}`];
            if (!cell || cell.v === undefined || cell.v === null || cell.v === '') continue;
            const amount = toNumber(cell.v);
            if (amount === 0) continue;
            const key = Math.abs(amount);
            absCount.set(key, (absCount.get(key) || 0) + 1);
        }

        // 탭2 목록화 시 사용할 원본 헤더행 탐색(P열에 '매출금액' 이 있는 행)
        let headerValues = [];
        for (let rowNum = 1; rowNum <= Math.min(lastRow, 30); rowNum++) {
            if (!cellText(worksheet, `P${rowNum}`).includes('매출금액')) continue;
            for (let c = 0; c <= lastCol; c++) {
                const cell = worksheet[XLSX.utils.encode_cell({r: rowNum - 1, c})];
                headerValues.push(cell && cell.v !== undefined && cell.v !== null ? cell.v : '');
            }
            break;
        }

        const rows = [];
        let postRowCount = 0;
        let minusRowCount = 0;
        for (let rowNum = 1; rowNum <= lastRow; rowNum++) {
            if (isHiddenRow(worksheet, rowNum)) continue;   // 숨김행 제외
            const merchant = cellText(worksheet, `M${rowNum}`);
            if (!POST_OFFICE_NAMES.some((name) => merchant === name)) continue;
            postRowCount++;
            const amount = toNumber(worksheet[`P${rowNum}`]?.v);
            if (amount >= 0) continue;
            minusRowCount++;
            if ((absCount.get(Math.abs(amount)) || 0) !== 1) continue; // 다른 row 에 동일금액 존재 → 제외

            // 탭2 목록화를 위해 해당 row 전체값 보존
            const rowValues = [];
            for (let c = 0; c <= lastCol; c++) {
                const addr = XLSX.utils.encode_cell({r: rowNum - 1, c});
                const cell = worksheet[addr];
                rowValues.push(cell && cell.v !== undefined && cell.v !== null ? cell.v : '');
            }
            rows.push({rowNum, merchant, amount, values: rowValues});
        }
        return {rows, total: rows.reduce((sum, r) => sum + r.amount, 0), postRowCount, minusRowCount, headerValues};
    };

    /**
     * 구매일지 파싱 (여러 탭 전체)
     * - 오프라인 수수료 : E~G 셀 중 '오프라인' 문구가 있는 셀의 바로 뒤 금액
     * - 이체대행/국내배송 수수료 : D열이 해당 유형인 row 의 E~G 셀에 있는 %(=금액원) 금액
     * - 국내배송 실결제(F13) : D열 '국내배송' && E~G 가 전체취소가 아닌 row 의 C열 합
     * - 이체대행 실결제(F16) : D열 '이체대행' && E~G 에 '취소' 문구가 없는 row 의 C열 합
     */
    const parsePurchaseLog = (workbook, includeSheetNames) => {
        const OFFLINE_RE = /오프라인[^0-9-]*([0-9,]+)\s*원/;
        const PERCENT_FEE_RE = /(\d+(?:\.\d+)?)\s*%\s*\(\s*=?\s*([0-9,]+)\s*원/;
        // E~G 셀들에서 정규식에 매칭되는 금액을 모두 합산 (루프 내 클로저 생성 방지를 위해 순수함수로 분리)
        const sumMatchedAmount = (cells, regex, groupIndex) => {
            let sum = 0;
            let count = 0;
            for (let i = 0; i < cells.length; i++) {
                const matched = cells[i] ? cells[i].match(regex) : null;
                if (matched) {
                    sum += toNumber(matched[groupIndex]);
                    count++;
                }
            }
            return {sum, count};
        };

        let offlineFee = 0, transferFee = 0, domesticFee = 0;
        let domesticPaySum = 0, transferPaySum = 0;
        let domesticRowCount = 0, domesticExcludedCount = 0;
        let transferRowCount = 0, transferExcludedCount = 0;
        let offlineRowCount = 0;
        const sheetStats = [];

        includeSheetNames.forEach((sheetName) => {
            const worksheet = workbook.Sheets[sheetName];
            if (!worksheet || !worksheet['!ref']) return;
            const lastRow = XLSX.utils.decode_range(worksheet['!ref']).e.r + 1;

            let sOffline = 0, sTransfer = 0, sDomestic = 0, sDomesticPay = 0, sTransferPay = 0;

            for (let rowNum = 1; rowNum <= lastRow; rowNum++) {
                if (isHiddenRow(worksheet, rowNum)) continue;   // 숨김행 제외
                const category = cellText(worksheet, `D${rowNum}`);
                const noteCells = ['E', 'F', 'G'].map((col) => cellText(worksheet, `${col}${rowNum}`));
                const noteJoined = noteCells.filter(Boolean).join(' ');
                if (!category && !noteJoined) continue;

                const payAmount = toNumber(worksheet[`C${rowNum}`]?.v);

                // 1) 오프라인 수수료 : 유형과 무관하게 E~G 중 '오프라인' 문구가 있는 셀에서 추출
                const offlineMatched = sumMatchedAmount(noteCells, OFFLINE_RE, 1);
                sOffline += offlineMatched.sum;
                offlineRowCount += offlineMatched.count;

                // 2) 이체대행
                if (category === '이체대행') {
                    transferRowCount++;
                    sTransfer += sumMatchedAmount(noteCells, PERCENT_FEE_RE, 2).sum;
                    // F16 : '취소' 문구가 있으면 실결제금액 합산 제외
                    if (hasAnyCancelText(noteJoined)) transferExcludedCount++;
                    else sTransferPay += payAmount;
                }

                // 3) 국내배송
                if (category === '국내배송') {
                    domesticRowCount++;
                    sDomestic += sumMatchedAmount(noteCells, PERCENT_FEE_RE, 2).sum;
                    // F13 : 부분취소는 포함, 전체취소는 제외
                    if (isFullCancelText(noteJoined)) domesticExcludedCount++;
                    else sDomesticPay += payAmount;
                }
            }

            offlineFee += sOffline;
            transferFee += sTransfer;
            domesticFee += sDomestic;
            domesticPaySum += sDomesticPay;
            transferPaySum += sTransferPay;
            sheetStats.push({
                sheetName, offlineFee: sOffline, transferFee: sTransfer, domesticFee: sDomestic,
                domesticPaySum: sDomesticPay, transferPaySum: sTransferPay,
            });
        });

        return {
            offlineFee, transferFee, domesticFee,
            domesticPaySum, transferPaySum,
            domesticRowCount, domesticExcludedCount,
            transferRowCount, transferExcludedCount,
            offlineRowCount,
            sheetStats,
        };
    };

    /** 청구서(EMS/FedEx/UPS)와 조회된 PT1 운송장 대조 → 청구오류폼 데이터 산출 */
    const buildBillErrorRows = (worksheet, carrier, pt1Items) => {
        const parsed = parseBillWorksheet(worksheet, carrier);
        const dbWaybills = new Set(
            (pt1Items || []).map((item) => String(item.outWayNum || '').trim()).filter(Boolean)
        );
        const errorRows = [];
        parsed.waybillMap.forEach((bill, waybill) => {
            if (dbWaybills.has(waybill)) return;
            errorRows.push({
                no: errorRows.length + 1,
                waybill,
                orderNumber: bill.excelOrderNo || '0',
                refOrderNumber: bill.excelRefOrderNo || '',
                shipPrice: 0,
                billedAmount: bill.billedAmount,
                billRows: bill.billRows,
                billRowCount: bill.billRows.length,
            });
        });
        return {
            parsed,
            errorRows,
            errorTotal: errorRows.reduce((sum, r) => sum + r.billedAmount, 0),
            matchedCount: parsed.uniqueWaybillCount - errorRows.length,
        };
    };

    /** 업로드폼 파일 처리 */
    const processVatUploadFile = async (formKey, file) => {
        const form = VAT_UPLOAD_FORMS.find((f) => f.key === formKey);
        if (!form || !file) return;

        const lowerName = (file.name || '').toLowerCase();
        if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
            patchVatUpload(formKey, {errorMsg: '엑셀 파일(.xlsx / .xls)만 업로드할 수 있습니다.', done: false});
            return;
        }

        patchVatUpload(formKey, {fileName: file.name, loading: true, errorMsg: '', done: false});

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, XLSX_READ_OPTIONS);

            /* ── 구매일지 : 여러 탭 전체 파싱 ── */
            if (form.purchaseLog) {
                const allSheets = workbook.SheetNames;
                // 조회기간(startDate~endDate)에 해당하는 월 시트만 기본선택 (없으면 전체선택)
                const startYm = toYearMonthNumber(vatStartDate);
                const endYm = toYearMonthNumber(vatEndDate);
                let defaultSheets = allSheets;
                if (startYm && endYm) {
                    const inRange = allSheets.filter((name) => {
                        const ym = parseLogSheetYearMonth(name);
                        if (!ym) return false;
                        const value = ym.year * 100 + ym.month;
                        return value >= startYm && value <= endYm;
                    });
                    if (inRange.length > 0) defaultSheets = inRange;
                }
                const result = parsePurchaseLog(workbook, defaultSheets);
                patchVatUpload(formKey, {
                    loading: false, done: true, sheetName: defaultSheets.join(', '),
                    allSheets, selectedSheets: defaultSheets, workbookRef: workbook,
                    logResult: result,
                    autoSelectedByPeriod: defaultSheets.length !== allSheets.length,
                });
                return;
            }

            /* ── 일반 업로드폼 : 금액열 총합 ── */
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            if (!worksheet) throw new Error('엑셀에서 시트를 찾을 수 없습니다.');

            const amountResult = parseAmountColumnTotal(worksheet, form.amountCol);
            const patch = {
                loading: false, done: true, sheetName,
                totalAmount: amountResult.total,
                valuedRowCount: amountResult.valuedRowCount,
                lastRow: amountResult.lastRow,
                hiddenRowCount: amountResult.hiddenRowCount,
                hiddenAmount: amountResult.hiddenAmount,
                firstVisibleRow: amountResult.firstVisibleRow,
                lastVisibleRow: amountResult.lastVisibleRow,
                srcSheetName: sheetName,
            };

            // 배송사 청구서 → 청구오류폼(운송장 미매칭) 산출
            if (form.carrierKey) {
                const carrier = BILL_CARRIERS[form.carrierKey];
                const baseItems = vatPt1Filtered.length > 0 ? vatPt1Filtered : vatPt1All;
                const billResult = buildBillErrorRows(worksheet, carrier, baseItems);
                patch.carrierKey = form.carrierKey;
                patch.billParsed = {
                    uniqueWaybillCount: billResult.parsed.uniqueWaybillCount,
                    matchedRowCount: billResult.parsed.matchedRowCount,
                    aggregatedRowCount: billResult.parsed.aggregatedRowCount,
                    filteredOutRowCount: billResult.parsed.filteredOutRowCount,
                    filteredOutAmount: billResult.parsed.filteredOutAmount,
                    aggregatedTotal: billResult.parsed.totalBilledAmount,
                };
                patch.errorRows = billResult.errorRows;
                patch.errorTotal = billResult.errorTotal;
                patch.matchedCount = billResult.matchedCount;
                patch.comparedAgainst = vatPt1Filtered.length > 0 ? 'FILTERED' : 'ALL';
            }

            // 비용카드내역 → 우체국 페이백 추출
            if (form.postPayback) {
                patch.payback = parsePostOfficePayback(worksheet);
            }

            /* 최종 세무제출 파일에 '업로드 원본 그대로'(서식/병합/숨김행 포함) 삽입할 대상은
             * 이 시점에 ExcelJS 로도 미리 파싱해 시트모델을 보관한다.
             * (카드내역처럼 병합이 2만건인 파일은 ExcelJS 로드에만 수십초가 걸리므로,
             *  [최종데이터 생성] 클릭 시점에 몰아서 하지 않고 업로드 시점으로 분산시킨다) */
            if (form.passThrough) {
                patchVatUpload(formKey, {...patch, loading: true, parsingOriginal: true});
                await sleep(60);
                const srcWorkbook = new ExcelJS.Workbook();
                await srcWorkbook.xlsx.load(arrayBuffer);
                const srcWorksheet = srcWorkbook.getWorksheet(sheetName) || srcWorkbook.worksheets[0];
                patch.excelSheetModel = srcWorksheet ? srcWorksheet.model : null;
                patch.parsingOriginal = false;
                patch.originalMergeCount = (patch.excelSheetModel?.merges || []).length;
            }

            patchVatUpload(formKey, patch);
        } catch (error) {
            console.error(`[부가세 업로드:${formKey}] 오류 : `, error);
            patchVatUpload(formKey, {
                loading: false, done: false,
                errorMsg: error?.message || '엑셀 파싱 중 오류가 발생했습니다.',
            });
        }
    };

    // 구매일지 탭 선택 변경 시 재집계
    const handleToggleLogSheet = (sheetName) => {
        const current = vatUploads.ORDER_LOG;
        if (!current || !current.workbookRef) return;
        const selected = current.selectedSheets.includes(sheetName)
            ? current.selectedSheets.filter((n) => n !== sheetName)
            : [...current.selectedSheets, sheetName];
        const ordered = current.allSheets.filter((n) => selected.includes(n));
        patchVatUpload('ORDER_LOG', {
            selectedSheets: ordered,
            sheetName: ordered.join(', '),
            logResult: parsePurchaseLog(current.workbookRef, ordered),
        });
    };

    const handleVatUploadClick = (formKey) => {
        vatUploadTargetRef.current = formKey;
        if (vatFileInputRef.current) vatFileInputRef.current.click();
    };
    const handleVatFileInputChange = async (event) => {
        const file = event.target.files?.[0];
        const target = vatUploadTargetRef.current;
        event.target.value = '';
        if (file && target) await processVatUploadFile(target, file);
    };
    const handleVatDrop = async (formKey, event) => {
        event.preventDefault();
        event.stopPropagation();
        setVatDraggingKey('');
        const file = event.dataTransfer?.files?.[0];
        if (file) await processVatUploadFile(formKey, file);
    };
    const handleVatReset = (formKey, event) => {
        if (event) event.stopPropagation();
        setVatUploads((prev) => {
            const next = {...prev};
            delete next[formKey];
            return next;
        });
    };

    /* region 최종 세무제출 엑셀 생성 (사항5~6) ---------------------------------- */
    const [finalGenLoading, setFinalGenLoading] = useState(false);
    const [finalGenLog, setFinalGenLog] = useState(null);

    // 템플릿 원본 시트명 (public/taxform/btorage-tax-template.xlsx)
    const TEMPLATE_SHEET_MAIN = '26년도 2분기';
    const TEMPLATE_SHEET_REFUND = '환불내역+용도외사용';
    const TEMPLATE_SHEET_BURDEN = '비토리지 부담(손해)';

    // yyyy-MM-dd → {yy:'26', month:4}
    const splitDateParts = (dateText) => {
        const [y, m] = String(dateText || '').split('-');
        return {yy: (y || '').slice(2), month: Number(m || 0)};
    };

    // yyyyMMdd → yyyy-MM-dd
    const formatYmd = (yyyymmdd) => {
        const text = String(yyyymmdd || '');
        if (text.length !== 8) return text;
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
    };

    /**
     * 업로드한 원본 워크시트를 최종 워크북에 '그대로' 복사
     * - 값/서식/열너비/행높이/숨김행/병합까지 원본 상태 그대로 이식한다.
     * - ExcelJS 의 worksheet.model 이식은 병합(merges)을 옮기지 못하므로 병합은 별도로 재적용한다.
     * - mergeCells 는 호출마다 기존 병합 전체와 교차검사(O(n^2))를 수행해 카드내역(2만건)에서 27초가 걸린다.
     *   원본의 병합범위는 서로 겹치지 않음이 보장되므로, 검사대상(_merges)을 매 호출 직전 비워 O(1)로 만들고
     *   결과만 누적한 뒤 마지막에 복원한다. (27,500ms → 110ms)
     */
    const copyWorksheetAsIs = (destWorkbook, srcModel, newName) => {
        const merges = srcModel.merges || [];
        const dest = destWorkbook.addWorksheet(newName);
        dest.model = Object.assign({}, srcModel, {name: newName, id: dest.id, orderNo: dest.orderNo});
        dest.name = newName;

        if (merges.length) {
            const accumulated = {};
            const mergeFn = typeof dest.mergeCellsWithoutStyle === 'function'
                ? dest.mergeCellsWithoutStyle.bind(dest)
                : dest.mergeCells.bind(dest);
            try {
                merges.forEach((range) => {
                    dest._merges = {};
                    mergeFn(range);
                    Object.assign(accumulated, dest._merges);
                });
            } catch (error) {
                console.warn('병합범위 복사 중 일부 실패 : ', error?.message);
            }
            dest._merges = accumulated;
        }
        return dest;
    };

    // 신규 시트 공통 타이틀 (가독성용)
    const addSheetTitle = (worksheet, title, colCount, fillColor) => {
        const row = worksheet.addRow([title]);
        row.height = 32;
        worksheet.mergeCells(row.number, 1, row.number, Math.max(colCount, 1));
        const cell = worksheet.getCell(row.number, 1);
        cell.font = {name: '맑은 고딕', size: 14, bold: true, color: {argb: 'FFFFFFFF'}};
        cell.alignment = {horizontal: 'center', vertical: 'middle'};
        cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: fillColor}};
        return row.number;
    };

    const generateFinalTaxFile = async () => {
        if (!canGenerateFinalData || finalGenLoading) return;
        setFinalGenLoading(true);
        setVatErrorMsg('');
        try {
            /* ── 0) 집계값 준비 ── */
            const log = vatUploads.ORDER_LOG?.logResult || {};
            const orderCardTotal = vatUploads.ORDER_CARD?.totalAmount || 0;
            const fedexBillTotal = vatUploads.FEDEX_BILL?.totalAmount || 0;
            const upsBillTotal = vatUploads.UPS_BILL?.totalAmount || 0;
            const payback = vatUploads.COST_CARD?.payback || {rows: [], total: 0, headerValues: []};

            const seaProfit = (vatAgg.seaCustomerSum || 0) - toNumber(manualSeaBilled);
            const kPackProfit = (vatAgg.kPackCustomerSum || 0) - toNumber(manualKPackBilled);
            const sPackProfit = (vatAgg.sPackCustomerSum || 0) - toNumber(manualSPackBilled);
            const anellFee = toNumber(manualAnellFee);

            // FedEx 차익 = (배송료+기타) − FedEx 청구서 BN열 총액
            //   ※ BN열 총액은 B열 문구(운송/관세·세금) 구분없이 청구서의 모든 표시행을 합산한 값이므로
            //     관세·세금 청구분과 DB 미매칭(청구오류) 운송장 청구분까지 모두 차감된다.
            const fedexProfit = (vatAgg.ttFedexPrice || 0) - fedexBillTotal;
            // UPS 차익 = (배송료+기타) − UPS 청구서 BA열 총액 (동일하게 전 표시행 합산)
            const upsProfit = (vatAgg.ttUpsPrice || 0) - upsBillTotal;

            const valF13 = log.domesticPaySum || 0;                       // 구매일지 국내배송 실결제(전체취소 제외)
            const valF11 = orderCardTotal - valF13;                       // 구매카드총액 − F13
            const valF12 = seaProfit + kPackProfit + sPackProfit
                + (vatAgg.overseasFeeTotal || 0) + fedexProfit + upsProfit
                + (log.offlineFee || 0) + anellFee;
            // F14 = domesticFormShipFee + 국내배송(오프라인+국내배송폼) 5종수수료 + 구매일지 국내배송 7% 수수료
            const valF14 = (vatAgg.domesticFormFeeTotal || 0) + (log.domesticFee || 0);
            // F15 = deliveryFormShipFee + 국내배송(배송대행폼) 5종수수료
            const valF15 = vatAgg.deliveryFormFeeTotal || 0;
            const valF16 = log.transferFee || 0;                          // 구매일지 이체대행 7% 수수료 총합
            const paybackTotal = Math.abs(payback.total || 0);

            /* ── 1) 템플릿 로드 (탭1 / 환불내역 / 비토리지부담 서식 그대로 사용) ── */
            const templateUrl = `${process.env.PUBLIC_URL || ''}/taxform/btorage-tax-template.xlsx`;
            const response = await fetch(templateUrl);
            if (!response.ok) throw new Error(`세무 양식 템플릿을 불러오지 못했습니다. (HTTP ${response.status})`);
            const templateBuffer = await response.arrayBuffer();

            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(templateBuffer);

            const mainSheet = workbook.getWorksheet(TEMPLATE_SHEET_MAIN);
            const refundSheet = workbook.getWorksheet(TEMPLATE_SHEET_REFUND);
            const burdenSheet = workbook.getWorksheet(TEMPLATE_SHEET_BURDEN);
            if (!mainSheet) throw new Error('템플릿에서 매출집계 시트를 찾을 수 없습니다.');

            /* ── 2) 탭1 : 지정 셀만 교체 (그 외 서식/내용 원본유지) ── */
            const startParts = splitDateParts(vatStartDate);
            const endParts = splitDateParts(vatEndDate);
            const periodLabel = `${startParts.yy}년${startParts.month}-${endParts.month}월`;

            mainSheet.getCell('F11').value = valF11;
            mainSheet.getCell('F12').value = valF12;
            mainSheet.getCell('F13').value = valF13;
            mainSheet.getCell('F14').value = valF14;
            mainSheet.getCell('F15').value = valF15;
            mainSheet.getCell('F16').value = valF16;
            // D24 는 병합(D24:E24) 문장셀 → 원본 문장 포맷을 유지하고 금액만 치환
            mainSheet.getCell('D24').value = `순수 우리가 받은 우체국 페이백 총금액: ${paybackTotal.toLocaleString()}원`;

            // 값 제거(공란) 대상
            for (let r = 28; r <= 36; r++) {
                mainSheet.getCell(`F${r}`).value = null;
                mainSheet.getCell(`G${r}`).value = null;
            }
            mainSheet.getCell('G37').value = null;
            for (let r = 43; r <= 53; r++) mainSheet.getCell(`E${r}`).value = null;
            for (let r = 43; r <= 57; r++) mainSheet.getCell(`G${r}`).value = null;

            mainSheet.name = periodLabel;

            /* ── 3) 탭9 / 탭10 : 템플릿 내용 그대로, 탭명만 정리 ── */
            if (refundSheet) refundSheet.name = '환불내역';
            if (burdenSheet) burdenSheet.name = '비토리지부담(손해)';

            /* ── 4) 탭2 : 우체국페이백 ── */
            const paybackSheet = workbook.addWorksheet('우체국페이백');
            const paybackColCount = Math.max(
                (payback.headerValues || []).length,
                payback.rows.length ? payback.rows[0].values.length : 0, 9
            );
            addSheetTitle(paybackSheet, `우체국 페이백 (${vatStartDate} ~ ${vatEndDate})`, paybackColCount, 'FF4B89DC');
            if ((payback.headerValues || []).length) {
                const headerRow = paybackSheet.addRow(payback.headerValues);
                headerRow.height = 24;
                headerRow.eachCell((cell) => {
                    cell.font = {name: '맑은 고딕', size: 10.5, bold: true};
                    cell.alignment = {horizontal: 'center', vertical: 'middle'};
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFF3F3F3'}};
                });
            }
            payback.rows.forEach((row) => paybackSheet.addRow(row.values));
            const paybackTotalRow = paybackSheet.addRow([]);
            paybackTotalRow.height = 28;
            paybackTotalRow.getCell(1).value = 'EMS 캐시백 순이익';
            paybackTotalRow.getCell(1).font = {name: '맑은 고딕', size: 12, bold: true};
            const paybackTotalCell = paybackTotalRow.getCell(Math.min(16, paybackColCount)); // P열(매출금액) 위치
            paybackTotalCell.value = payback.total;
            paybackTotalCell.numFmt = '#,##0';
            paybackTotalCell.font = {name: '맑은 고딕', size: 12, bold: true, color: {argb: 'FF16A34A'}};
            paybackTotalRow.eachCell((cell) => {
                cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFF3DC'}};
                cell.border = {top: {style: 'medium', color: {argb: 'FFFFAF34'}}};
            });
            paybackSheet.getColumn(13).width = 24;
            paybackSheet.getColumn(16).width = 16;

            /* ── 5) 탭3~탭7 : 관리자가 업로드한 파일을 원본 서식/숨김행 그대로 이식 ── */
            const passThroughSheets = [
                {name: '우체국 소포수령증', key: 'EMS_BILL'},
                {name: 'FedEx 청구서', key: 'FEDEX_BILL'},
                {name: 'UPS 청구서', key: 'UPS_BILL'},
                {name: '구매카드내역', key: 'ORDER_CARD'},
                {name: '해외카드내역', key: 'OVERSEA_CARD'},
            ];
            // 업로드 시점에 미리 파싱해둔 원본 시트모델을 그대로 이식(파일당 수십~수백ms)
            passThroughSheets.forEach((info) => {
                const srcModel = vatUploads[info.key]?.excelSheetModel;
                if (srcModel) copyWorksheetAsIs(workbook, srcModel, info.name);
            });

            /* ── 6) 탭8 : 적립금사용내역 ── */
            const pointSheet = workbook.addWorksheet('적립금사용내역');
            addSheetTitle(pointSheet, `적립금 사용/취소 내역 (${vatStartDate} ~ ${vatEndDate})`, 5, 'FFFFAF34');
            const pointHeader = pointSheet.addRow(['번호', '날짜', '성함', '구분', '사용적립금']);
            pointHeader.height = 24;
            pointHeader.eachCell((cell) => {
                cell.font = {name: '맑은 고딕', size: 11, bold: true};
                cell.alignment = {horizontal: 'center', vertical: 'middle'};
                cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFF3F3F3'}};
                cell.border = {bottom: {style: 'medium', color: {argb: 'FFFFAF34'}}};
            });
            let pointTotal = 0;
            vatUsePointList.forEach((item, index) => {
                // dacType 0 = 취소(금액 앞 '-') / 1 = 사용(금액 그대로)
                const isCancel = String(item.type) === '0';
                const price = item.ptPrice || 0;
                const signedPrice = isCancel ? -Math.abs(price) : Math.abs(price);
                pointTotal += signedPrice;
                const row = pointSheet.addRow([
                    index + 1,
                    formatYmd(item.regDate),
                    item.memberDTO?.userName || '',
                    isCancel ? '취소' : '사용',
                    signedPrice,
                ]);
                row.getCell(5).numFmt = '#,##0';
                row.eachCell((cell, c) => {
                    cell.font = {name: '맑은 고딕', size: 10};
                    cell.alignment = {horizontal: c === 5 ? 'right' : 'center', vertical: 'middle'};
                });
            });
            const pointTotalRow = pointSheet.addRow(['', '', '', '합계', pointTotal]);
            pointTotalRow.height = 26;
            pointTotalRow.getCell(5).numFmt = '#,##0';
            pointTotalRow.eachCell((cell) => {
                cell.font = {name: '맑은 고딕', size: 11, bold: true};
                cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFF3DC'}};
                cell.border = {top: {style: 'medium', color: {argb: 'FFFFAF34'}}};
            });
            [8, 14, 18, 10, 16].forEach((w, i) => { pointSheet.getColumn(i + 1).width = w; });

            /* ── 7) 탭 순서 정렬 (탭1 → 탭10) ── */
            const finalOrder = [
                periodLabel, '우체국페이백', '우체국 소포수령증', 'FedEx 청구서', 'UPS 청구서',
                '구매카드내역', '해외카드내역', '적립금사용내역', '환불내역', '비토리지부담(손해)',
            ];
            finalOrder.forEach((name, index) => {
                const sheet = workbook.getWorksheet(name);
                if (sheet) sheet.orderNo = index + 1;
            });

            /* ── 8) 저장 ── */
            const buffer = await workbook.xlsx.writeBuffer();
            const fileName = `비토리지매출 ${periodLabel}.xlsx`;
            saveAs(new Blob([buffer], {type: 'application/octet-stream'}), fileName);

            setFinalGenLog({
                fileName,
                periodLabel,
                sheetNames: workbook.worksheets.map((w) => w.name),
                valF11, valF12, valF13, valF14, valF15, valF16,
                paybackTotal, paybackRowCount: payback.rows.length,
                seaProfit, kPackProfit, sPackProfit, fedexProfit, upsProfit,
                overseasFeeTotal: vatAgg.overseasFeeTotal || 0,
                offlineFee: log.offlineFee || 0,
                logDomesticFee: log.domesticFee || 0,
                anellFee,
                orderCardTotal, fedexBillTotal, upsBillTotal,
                pointTotal, pointCount: vatUsePointList.length,
                generatedAt: new Date().toLocaleString(),
            });
        } catch (error) {
            console.error('[부가세] 최종데이터 생성 오류 : ', error);
            setVatErrorMsg(error?.message || '최종 세무제출 파일 생성 중 오류가 발생했습니다.');
        } finally {
            setFinalGenLoading(false);
        }
    };
    /* endregion */

    /* 수동입력폼 자동계산 */
    const manualSeaProfit = (vatAgg?.seaCustomerSum || 0) - toNumber(manualSeaBilled);
    const manualKPackProfit = (vatAgg?.kPackCustomerSum || 0) - toNumber(manualKPackBilled);
    const manualSPackProfit = (vatAgg?.sPackCustomerSum || 0) - toNumber(manualSPackBilled);

    // 업로드폼 7종이 모두 완료되었는지 (사항5 : [최종데이터 생성] 버튼 렌더조건)
    const isAllVatUploadDone = VAT_UPLOAD_FORMS.every((form) => vatUploads[form.key]?.done);
    const canGenerateFinalData = vatFilterDone && vatPt1Filtered.length > 0 && isAllVatUploadDone;
    /* endregion */

    /* region DB 조회함수 (배송사별 2종 : 개발 baseURL / 운영 full 경로) ---------- */

    // [EMS / 개발] axiosInstance 의 baseURL(https://btorage-dev.com/api) 사용
    const selectedEmsShipBillData = async (waybillNumbers) => {
        const response = await axios.post('/order/selectedShipBillData', waybillNumbers);
        return response.data || [];
    };
    // [EMS / 운영] ⚠ 임시 - 운영서버 full 경로 직접조회 (운영데이터 검증완료 후 제거예정)
    const selectedEmsShipBillDataForProd = async (waybillNumbers) => {
        const response = await axios.post(`${PROD_API_BASE_URL}/order/selectedShipBillData`, waybillNumbers, {
            headers: {'Content-Type': 'application/json'},
        });
        return response.data || [];
    };

    // [FedEx / 개발]
    const selectedFedexShipBillData = async (waybillNumbers) => {
        const response = await axios.post('/order/selectedShipBillData', waybillNumbers);
        return response.data || [];
    };
    // [FedEx / 운영] ⚠ 임시
    const selectedFedexShipBillDataForProd = async (waybillNumbers) => {
        const response = await axios.post(`${PROD_API_BASE_URL}/order/selectedShipBillData`, waybillNumbers, {
            headers: {'Content-Type': 'application/json'},
        });
        return response.data || [];
    };

    // [UPS / 개발]
    const selectedUpsShipBillData = async (waybillNumbers) => {
        const response = await axios.post('/order/selectedShipBillData', waybillNumbers);
        return response.data || [];
    };
    // [UPS / 운영] ⚠ 임시
    const selectedUpsShipBillDataForProd = async (waybillNumbers) => {
        const response = await axios.post(`${PROD_API_BASE_URL}/order/selectedShipBillData`, waybillNumbers, {
            headers: {'Content-Type': 'application/json'},
        });
        return response.data || [];
    };

    // 현재 선택된 조회서버 + 배송사에 맞는 조회함수 반환
    const resolveShipBillFetcher = (carrierKey) => {
        const isProd = billDataSource === 'PROD';
        if (carrierKey === 'EMS') return isProd ? selectedEmsShipBillDataForProd : selectedEmsShipBillData;
        if (carrierKey === 'FEDEX') return isProd ? selectedFedexShipBillDataForProd : selectedFedexShipBillData;
        return isProd ? selectedUpsShipBillDataForProd : selectedUpsShipBillData;
    };

    /* endregion */

    /* region 청구서 엑셀 파싱 --------------------------------------------------
     * 운송장번호 열을 전체 행 스캔하면서 패턴에 맞는 행만 데이터로 인정하고,
     * 동일 운송장번호가 여러 행에 존재하면 금액을 모두 합산한다.
     *  - EMS   : 등기번호별 총요금(I열)
     *  - FedEx : B열이 '운송'인 행만 BN열 합산 (관세/세금 행은 실청구액에서 제외)
     *  - UPS   : 동일 운송장이 FRT/FSC/ACC/TAX/INF 요금유형별 행으로 나뉘어 청구 → 합산이 정상
     * ---------------------------------------------------------------------- */
    const parseBillWorksheet = (worksheet, carrier) => {
        const ref = worksheet['!ref'];
        if (!ref) throw new Error('엑셀 시트가 비어있습니다. (시트 범위 정보 없음)');

        const range = XLSX.utils.decode_range(ref);
        const lastRow = range.e.r + 1;

        const waybillMap = new Map();
        let valuedRowCount = 0;     // 운송장 열에 값이 있는 행수
        let matchedRowCount = 0;    // 운송장 패턴에 매칭된 행수
        let skippedRowCount = 0;    // 값은 있으나 패턴 불일치로 제외(헤더 등)
        let aggregatedRowCount = 0; // 실제 금액집계에 사용된 행수(rowFilter 통과)
        let filteredOutRowCount = 0;// rowFilter 로 제외된 행수(FedEx 관세/세금 등)
        let filteredOutAmount = 0;  // rowFilter 로 제외된 금액합
        let totalBilledAmount = 0;  // 집계대상 금액 총합
        const skippedSamples = [];
        const filteredOutWaybills = new Set();

        for (let rowNum = 1; rowNum <= lastRow; rowNum++) {
            if (isHiddenRow(worksheet, rowNum)) continue;   // 숨김행 제외

            // 운송장번호 (UPS 는 N열이 비면 U열로 대체)
            let rawWaybill = cellText(worksheet, `${carrier.waybillCol}${rowNum}`);
            if ((!rawWaybill || !carrier.waybillPattern.test(rawWaybill)) && carrier.altWaybillCol) {
                const altWaybill = cellText(worksheet, `${carrier.altWaybillCol}${rowNum}`);
                if (altWaybill && carrier.waybillPattern.test(altWaybill)) rawWaybill = altWaybill;
            }
            if (!rawWaybill) continue;
            valuedRowCount++;

            if (!carrier.waybillPattern.test(rawWaybill)) {
                skippedRowCount++;
                if (skippedSamples.length < 5) skippedSamples.push(`${carrier.waybillCol}${rowNum} : ${rawWaybill}`);
                continue;
            }
            matchedRowCount++;

            const amount = toNumber(worksheet[`${carrier.amountCol}${rowNum}`]?.v);
            const typeValue = carrier.typeCol ? cellText(worksheet, `${carrier.typeCol}${rowNum}`) : '';

            // 행 필터(FedEx : B열 '운송' 만 실청구액 집계)
            if (carrier.rowFilter && !carrier.rowFilter(typeValue)) {
                filteredOutRowCount++;
                filteredOutAmount += amount;
                filteredOutWaybills.add(rawWaybill);
                continue;
            }
            aggregatedRowCount++;
            totalBilledAmount += amount;

            if (!waybillMap.has(rawWaybill)) {
                waybillMap.set(rawWaybill, {
                    waybill: rawWaybill,
                    billedAmount: 0,
                    billRows: [],
                    excelOrderNo: '',
                    excelRefOrderNo: '',
                });
            }
            const target = waybillMap.get(rawWaybill);
            target.billedAmount += amount;
            target.billRows.push({rowNum, amount, type: typeValue || '-'});

            // 집계목록오류 출력용 주문번호 (엑셀에서 추출)
            if (!target.excelOrderNo && carrier.orderNoCol) {
                const orderNo = cellText(worksheet, `${carrier.orderNoCol}${rowNum}`);
                if (orderNo && (!carrier.orderNoPrefix || orderNo.startsWith(carrier.orderNoPrefix))) {
                    target.excelOrderNo = orderNo;
                }
            }
            if (!target.excelRefOrderNo && carrier.refOrderNoCol) {
                target.excelRefOrderNo = cellText(worksheet, `${carrier.refOrderNoCol}${rowNum}`);
            }
        }

        // rowFilter 로 인해 집계목록에서 완전히 빠진 운송장(= 운송행이 하나도 없는 운송장)
        let onlyFilteredWaybillCount = 0;
        filteredOutWaybills.forEach((waybill) => {
            if (!waybillMap.has(waybill)) onlyFilteredWaybillCount++;
        });

        return {
            waybillMap,
            lastRow,
            valuedRowCount,
            matchedRowCount,
            skippedRowCount,
            skippedSamples,
            aggregatedRowCount,
            filteredOutRowCount,
            filteredOutAmount,
            onlyFilteredWaybillCount,
            totalBilledAmount,
            uniqueWaybillCount: waybillMap.size,
        };
    };
    /* endregion */

    /* region 청구서 업로드 → 조회 → 집계 파이프라인 ----------------------------- */
    const processBillFile = async (carrierKey, file) => {
        const carrier = BILL_CARRIERS[carrierKey];
        if (!file) return;

        const lowerName = (file.name || '').toLowerCase();
        if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
            patchBill(carrierKey, {
                ...createInitialBillState(),
                errorMsg: '엑셀 파일(.xlsx / .xls)만 업로드할 수 있습니다.',
            });
            return;
        }

        setExpandedRowKey('');
        patchBill(carrierKey, {
            ...createInitialBillState(),
            fileName: file.name,
            isProcessing: true,
            progress: 5,
            progressLabel: '파일을 읽는 중입니다...',
        });

        try {
            await sleep(120);

            /* 1) 엑셀 로드 */
            const arrayBuffer = await file.arrayBuffer();
            patchBill(carrierKey, {progress: 20, progressLabel: '엑셀 파일을 분석하는 중입니다...'});
            await sleep(120);

            const workbook = XLSX.read(arrayBuffer, XLSX_READ_OPTIONS);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            if (!worksheet) throw new Error('엑셀에서 시트를 찾을 수 없습니다.');

            /* 2) 운송장번호 / 청구금액 추출 및 중복합산 */
            patchBill(carrierKey, {
                sheetName,
                progress: 38,
                progressLabel: `운송장번호(${carrier.waybillCol}열) / 청구운송료(${carrier.amountCol}열) 추출 중...`,
            });
            await sleep(140);

            const parsed = parseBillWorksheet(worksheet, carrier);
            if (parsed.uniqueWaybillCount === 0) {
                throw new Error(
                    `${carrier.waybillCol}열에서 ${carrier.label} 운송장번호(${carrier.waybillPatternDesc})를 찾지 못했습니다. 업로드한 청구서 양식을 확인해주세요.`
                );
            }

            /* 3) DB 조회 (중복 제거된 운송장번호로 1건씩만 조회) */
            patchBill(carrierKey, {
                progress: 55,
                progressLabel: `중복제거된 운송장번호 ${formatNum(parsed.uniqueWaybillCount)}건으로 데이터베이스 조회 중...`,
            });
            await sleep(140);

            const waybillList = Array.from(parsed.waybillMap.keys());
            const fetchShipBillData = resolveShipBillFetcher(carrierKey);
            const dbList = await fetchShipBillData(waybillList);

            /* 4) 매칭 및 집계 */
            patchBill(carrierKey, {progress: 82, progressLabel: '청구서와 데이터베이스 데이터를 대조/집계하는 중...'});
            await sleep(140);

            const dbMap = new Map();
            (dbList || []).forEach((item) => {
                if (!item || !item.outWayNum) return;
                const key = String(item.outWayNum).trim();
                const exists = dbMap.get(key);
                if (!exists || payStatusPriority(item.payStatus) < payStatusPriority(exists.payStatus)) {
                    dbMap.set(key, item);
                }
            });

            const rows = [];      // 집계목록 (DB 매칭)
            const errorRows = []; // 집계목록오류 (DB 미매칭)

            parsed.waybillMap.forEach((bill, waybill) => {
                const db = dbMap.get(waybill);

                // 유형별 합산근거
                const typeBreakdown = {};
                bill.billRows.forEach((r) => {
                    typeBreakdown[r.type] = (typeBreakdown[r.type] || 0) + r.amount;
                });

                const base = {
                    waybill,
                    billedAmount: bill.billedAmount,           // 실청구액
                    billRows: bill.billRows,
                    billRowCount: bill.billRows.length,
                    typeBreakdown,
                    excelOrderNo: bill.excelOrderNo,
                    excelRefOrderNo: bill.excelRefOrderNo,
                };

                if (db) {
                    const ttShipExpense = db.ttShipExpense || 0;  // 고객총결제
                    const shipPrice = db.shipPrice || 0;          // 예상송료
                    rows.push({
                        ...base,
                        no: rows.length + 1,
                        matched: true,
                        orderNumber: db.orderNumber || '',
                        boxNum: db.boxNum || '',
                        orderBoxNo: `${db.orderNumber || ''}-${db.boxNum || ''}`,
                        regNm: db.regNm || '',
                        dvType: db.dvType,
                        shipcoLabel: convertToShipco(db.dvType, db.shipco),
                        payStatus: db.payStatus,
                        ttShipExpense,
                        shipPrice,
                        profit: ttShipExpense - bill.billedAmount,  // 수익
                    });
                } else {
                    // 청구서에는 운송장/실청구액이 있으나 DB에서 찾지 못한 데이터
                    errorRows.push({
                        ...base,
                        no: errorRows.length + 1,
                        matched: false,
                        orderNumber: bill.excelOrderNo || '0',
                        refOrderNumber: bill.excelRefOrderNo || '',
                        shipPrice: 0,   // 예상송료 : 0 고정
                    });
                }
            });

            const sumTtShipExpense = rows.reduce((sum, r) => sum + r.ttShipExpense, 0);
            const sumShipPrice = rows.reduce((sum, r) => sum + r.shipPrice, 0);
            const sumBilledAmount = rows.reduce((sum, r) => sum + r.billedAmount, 0);
            const sumProfit = rows.reduce((sum, r) => sum + r.profit, 0);
            const sumErrorBilledAmount = errorRows.reduce((sum, r) => sum + r.billedAmount, 0);

            const summary = {
                sheetName,
                lastRow: parsed.lastRow,
                valuedRowCount: parsed.valuedRowCount,
                matchedRowCount: parsed.matchedRowCount,
                skippedRowCount: parsed.skippedRowCount,
                skippedSamples: parsed.skippedSamples,
                aggregatedRowCount: parsed.aggregatedRowCount,
                filteredOutRowCount: parsed.filteredOutRowCount,
                filteredOutAmount: parsed.filteredOutAmount,
                onlyFilteredWaybillCount: parsed.onlyFilteredWaybillCount,
                uniqueWaybillCount: parsed.uniqueWaybillCount,
                duplicatedRowCount: parsed.aggregatedRowCount - parsed.uniqueWaybillCount,
                excelTotalAmount: parsed.totalBilledAmount,
                dbMatchedCount: rows.length,
                dbUnMatchedCount: errorRows.length,
                sumTtShipExpense,
                sumShipPrice,
                sumBilledAmount,
                sumProfit,
                sumErrorBilledAmount,
                // 정합성 검증 : 엑셀 집계대상 총합 === (집계목록 실청구액 합 + 집계목록오류 실청구액 합)
                amountVerified:
                    Math.round(parsed.totalBilledAmount) === Math.round(sumBilledAmount + sumErrorBilledAmount),
                dataSource: billDataSource,
                queriedAt: new Date().toLocaleString(),
            };

            patchBill(carrierKey, {progress: 100, progressLabel: '집계가 완료되었습니다.'});
            await sleep(200);
            patchBill(carrierKey, {
                rows,
                errorRows,
                summary,
                isProcessing: false,
                isDone: true,
                progress: 100,
                progressLabel: '집계 완료',
                errorMsg: '',
            });
        } catch (error) {
            console.error(`[${carrierKey}] 청구서 집계 오류 : `, error);
            const message = error?.response?.status
                ? `데이터베이스 조회에 실패했습니다. (HTTP ${error.response.status}) 백엔드 배포상태를 확인해주세요.`
                : (error?.message || '청구서 집계 중 오류가 발생했습니다.');
            patchBill(carrierKey, {
                isProcessing: false,
                isDone: false,
                progress: 100,
                progressLabel: '오류',
                errorMsg: message,
            });
        }
    };
    /* endregion */

    /* region 업로드 이벤트(클릭 / 드래그) --------------------------------------- */
    const handleDropzoneClick = (carrierKey) => {
        const ref = getFileInputRef(carrierKey);
        if (ref.current) ref.current.click();
    };

    const handleFileInputChange = async (carrierKey, event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // 동일 파일 재선택 허용
        if (file) await processBillFile(carrierKey, file);
    };

    const handleDragOver = (carrierKey, event) => {
        event.preventDefault();
        event.stopPropagation();
        if (draggingKey !== carrierKey) setDraggingKey(carrierKey);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingKey('');
    };

    const handleDrop = async (carrierKey, event) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingKey('');
        const file = event.dataTransfer?.files?.[0];
        if (file) await processBillFile(carrierKey, file);
    };

    const handleResetBill = (carrierKey, event) => {
        if (event) event.stopPropagation();
        setExpandedRowKey('');
        patchBill(carrierKey, createInitialBillState());
    };
    /* endregion */

    /* region 집계목록 엑셀 다운로드 --------------------------------------------- */
    const applyBodyStyle = (worksheet, headerRowNum, totalRowNum, colCount, profitColIdx, moneyColIdxs, leftColIdxs, matchColIdx) => {
        for (let r = headerRowNum + 1; r <= worksheet.rowCount; r++) {
            const row = worksheet.getRow(r);
            const isTotal = totalRowNum && r === totalRowNum;
            for (let c = 1; c <= colCount; c++) {
                const cell = row.getCell(c);
                const isMoney = moneyColIdxs.includes(c);
                cell.font = {
                    name: '맑은 고딕',
                    size: isTotal ? 11 : 10,
                    bold: isTotal || c === profitColIdx,
                    color: {
                        argb: c === profitColIdx && typeof cell.value === 'number'
                            ? (cell.value < 0 ? 'FFE05E6D' : 'FF16A34A')
                            : 'FF3A3A3A',
                    },
                };
                cell.alignment = {
                    horizontal: isMoney ? 'right' : (leftColIdxs.includes(c) ? 'left' : 'center'),
                    vertical: 'middle',
                };
                if (isMoney) cell.numFmt = '#,##0';
                cell.border = {
                    top: isTotal ? {style: 'medium', color: {argb: 'FFFFAF34'}} : {style: 'hair', color: {argb: 'FFE5E5E5'}},
                    bottom: {style: 'hair', color: {argb: 'FFE5E5E5'}},
                    left: {style: 'hair', color: {argb: 'FFE5E5E5'}},
                    right: {style: 'hair', color: {argb: 'FFE5E5E5'}},
                };
                if (isTotal) {
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFF3DC'}};
                } else if (matchColIdx && row.getCell(matchColIdx).value === 'X') {
                    cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFDEDEF'}};
                }
            }
        }
    };

    const addTitleRows = (worksheet, colCount, title, subTitle, fillColor) => {
        const titleRow = worksheet.addRow([title]);
        titleRow.height = 36;
        worksheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
        const titleCell = worksheet.getCell(titleRow.number, 1);
        titleCell.font = {name: '맑은 고딕', size: 16, bold: true, color: {argb: 'FFFFFFFF'}};
        titleCell.alignment = {horizontal: 'center', vertical: 'middle'};
        titleCell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: fillColor}};

        const subRow = worksheet.addRow([subTitle]);
        subRow.height = 24;
        worksheet.mergeCells(subRow.number, 1, subRow.number, colCount);
        const subCell = worksheet.getCell(subRow.number, 1);
        subCell.font = {name: '맑은 고딕', size: 10, color: {argb: 'FF666666'}};
        subCell.alignment = {horizontal: 'left', vertical: 'middle'};
        subCell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFFFF7E8'}};
    };

    const styleHeaderRow = (headerRow) => {
        headerRow.height = 28;
        headerRow.eachCell((cell) => {
            cell.font = {name: '맑은 고딕', size: 11, bold: true, color: {argb: 'FF3A3A3A'}};
            cell.alignment = {horizontal: 'center', vertical: 'middle'};
            cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFF3F3F3'}};
            cell.border = {
                top: {style: 'thin', color: {argb: 'FFD0D0D0'}},
                bottom: {style: 'medium', color: {argb: 'FFFFAF34'}},
                left: {style: 'thin', color: {argb: 'FFD0D0D0'}},
                right: {style: 'thin', color: {argb: 'FFD0D0D0'}},
            };
        });
    };

    const downloadBillExcel = async (carrierKey) => {
        const carrier = BILL_CARRIERS[carrierKey];
        const state = billData[carrierKey];
        if (!state || (!state.rows.length && !state.errorRows.length)) return;
        const s = state.summary;

        const workbook = new ExcelJS.Workbook();
        workbook.creator = 'Btorage';

        const subTitle =
            `파일 : ${state.fileName}  |  시트 : ${state.sheetName}  |  집계대상 총 청구액 : ${formatWon(s.excelTotalAmount)}  |  ` +
            `고유 운송장 : ${formatNum(s.uniqueWaybillCount)}건(중복 ${formatNum(s.duplicatedRowCount)}행 합산)  |  ` +
            `DB매칭 : ${formatNum(s.dbMatchedCount)}건 / 미매칭 : ${formatNum(s.dbUnMatchedCount)}건  |  ` +
            `조회서버 : ${s.dataSource === 'PROD' ? '운영' : '개발'}  |  생성 : ${s.queriedAt}`;

        /* ---------- 시트1 : 집계목록 ---------- */
        const ws1 = workbook.addWorksheet(`${carrier.label} 집계목록`);
        ws1.columns = [
            {key: 'no', width: 7}, {key: 'orderBoxNo', width: 28}, {key: 'regNm', width: 16},
            {key: 'shipcoLabel', width: 13}, {key: 'waybill', width: 24},
            {key: 'ttShipExpense', width: 16}, {key: 'shipPrice', width: 16},
            {key: 'billedAmount', width: 16}, {key: 'profit', width: 16},
            {key: 'billRowCount', width: 15}, {key: 'billRowNums', width: 40},
        ];
        const COL1 = 11;
        addTitleRows(ws1, COL1, `${carrier.label} 배송료청구서 집계목록`, subTitle, 'FFFFAF34');
        const header1 = ws1.addRow([
            'No', '주문번호', '성함', '배송사', '운송장번호',
            '고객총결제', '예상송료', '실청구액', '수익', '합산행수', '합산근거(엑셀 행번호)',
        ]);
        styleHeaderRow(header1);
        const header1Num = header1.number;

        state.rows.forEach((row) => {
            ws1.addRow({
                no: row.no,
                orderBoxNo: row.orderBoxNo,
                regNm: row.regNm,
                shipcoLabel: row.shipcoLabel,
                waybill: row.waybill,
                ttShipExpense: row.ttShipExpense,
                shipPrice: row.shipPrice,
                billedAmount: row.billedAmount,
                profit: row.profit,
                billRowCount: row.billRowCount,
                billRowNums: row.billRows.map((r) => `${r.rowNum}행[${r.type}] ${formatNum(r.amount)}`).join(' + '),
            });
        });
        const total1 = ws1.addRow({
            no: '합계', orderBoxNo: `${formatNum(state.rows.length)}건`, regNm: '', shipcoLabel: '', waybill: '',
            ttShipExpense: s.sumTtShipExpense, shipPrice: s.sumShipPrice,
            billedAmount: s.sumBilledAmount, profit: s.sumProfit,
            billRowCount: '', billRowNums: '',
        });
        total1.height = 30;
        applyBodyStyle(ws1, header1Num, total1.number, COL1, 9, [6, 7, 8, 9], [11], null);
        ws1.views = [{state: 'frozen', ySplit: header1Num}];
        ws1.autoFilter = {from: {row: header1Num, column: 1}, to: {row: header1Num, column: COL1}};

        /* ---------- 시트2 : 집계목록오류 ---------- */
        const ws2 = workbook.addWorksheet(`${carrier.label} 집계목록오류`);
        const useRefCol = !!carrier.refOrderNoCol;
        const cols2 = [
            {key: 'no', width: 7}, {key: 'orderNumber', width: 26},
        ];
        if (useRefCol) cols2.push({key: 'refOrderNumber', width: 26});
        cols2.push(
            {key: 'waybill', width: 24}, {key: 'shipPrice', width: 14},
            {key: 'billedAmount', width: 16}, {key: 'billRowCount', width: 15},
            {key: 'billRowNums', width: 40},
        );
        ws2.columns = cols2;
        const COL2 = cols2.length;
        addTitleRows(
            ws2, COL2, `${carrier.label} 집계목록오류 (청구서에는 있으나 DB에서 찾지 못한 운송장)`,
            `${subTitle}  |  오류 실청구액 합계 : ${formatWon(s.sumErrorBilledAmount)}`,
            'FFE05E6D'
        );
        const header2Cells = ['No', `주문번호(${carrier.orderNoCol}열)`];
        if (useRefCol) header2Cells.push(carrier.refOrderNoLabel);
        header2Cells.push(`운송장번호(${carrier.waybillCol}열)`, '예상송료', '실청구액', '합산행수', '합산근거(엑셀 행번호)');
        const header2 = ws2.addRow(header2Cells);
        styleHeaderRow(header2);
        const header2Num = header2.number;

        state.errorRows.forEach((row) => {
            const data = {
                no: row.no,
                orderNumber: row.orderNumber,
                waybill: row.waybill,
                shipPrice: 0,
                billedAmount: row.billedAmount,
                billRowCount: row.billRowCount,
                billRowNums: row.billRows.map((r) => `${r.rowNum}행[${r.type}] ${formatNum(r.amount)}`).join(' + '),
            };
            if (useRefCol) data.refOrderNumber = row.refOrderNumber || '';
            ws2.addRow(data);
        });
        if (state.errorRows.length > 0) {
            const moneyIdx2 = useRefCol ? [5, 6] : [4, 5];
            const total2 = ws2.addRow({
                no: '합계',
                orderNumber: `${formatNum(state.errorRows.length)}건`,
                refOrderNumber: '',
                waybill: '',
                shipPrice: 0,
                billedAmount: s.sumErrorBilledAmount,
                billRowCount: '',
                billRowNums: '',
            });
            total2.height = 30;
            applyBodyStyle(ws2, header2Num, total2.number, COL2, -1, moneyIdx2, [COL2], null);
        } else {
            ws2.addRow({no: '', orderNumber: '집계목록오류 데이터가 없습니다.'});
        }
        ws2.views = [{state: 'frozen', ySplit: header2Num}];

        const buffer = await workbook.xlsx.writeBuffer();
        const now = new Date();
        const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        saveAs(new Blob([buffer], {type: 'application/octet-stream'}), `배송료청구서집계_${carrier.label}_${stamp}.xlsx`);
    };
    /* endregion */

    /* region 렌더 헬퍼 ---------------------------------------------------------- */
    const currentCarrier = BILL_CARRIERS[billCarrierTab];
    const currentBill = billData[billCarrierTab];

    const carrierBadge = useMemo(() => ({
        EMS: billData.EMS.rows.length + billData.EMS.errorRows.length,
        FEDEX: billData.FEDEX.rows.length + billData.FEDEX.errorRows.length,
        UPS: billData.UPS.rows.length + billData.UPS.errorRows.length,
    }), [billData]);

    const renderDropzone = (carrierKey) => {
        const carrier = BILL_CARRIERS[carrierKey];
        const state = billData[carrierKey];
        const isDragging = draggingKey === carrierKey;

        return (
            <div
                className={`rtx-dropzone${isDragging ? ' is-dragging' : ''}${state.isDone ? ' is-done' : ''}`}
                onClick={() => handleDropzoneClick(carrierKey)}
                onDragOver={(e) => handleDragOver(carrierKey, e)}
                onDragEnter={(e) => handleDragOver(carrierKey, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(carrierKey, e)}
            >
                {state.isDone ? (
                    <>
                        <p className="rtx-dropzone-title" style={{color: '#2f9e44'}}>업로드 완료</p>
                        <p className="rtx-dropzone-desc">
                            <b>{state.fileName}</b>{state.sheetName ? ` (시트 : ${state.sheetName})` : ''}
                        </p>
                        <p className="rtx-dropzone-desc">다른 파일을 올리려면 이 영역을 다시 클릭하거나 파일을 드래그하세요.</p>
                        <button className="admin-btn-B wd-100 ht-30 font-s-13 m-t-10"
                                onClick={(e) => handleResetBill(carrierKey, e)}>초기화
                        </button>
                    </>
                ) : (
                    <>
                        <Lottie animationData={taxExcel} loop autoplay className="opa06 wd-110 ht-110"/>
                        <p className="rtx-dropzone-title">클릭 및 드래그로 파일을 업로드하세요</p>
                        <p className="rtx-dropzone-desc">
                            {carrier.fullLabel} 엑셀(.xlsx / .xls)을 이 영역에 끌어다 놓거나 클릭해서 선택하세요.
                        </p>
                        <span className="rtx-dropzone-chip">
                            운송장번호 : {carrier.waybillCol}열({carrier.waybillPatternDesc})
                            &nbsp;·&nbsp; 청구운송료 : {carrier.amountCol}열
                            {carrier.rowFilterDesc ? ` · ${carrier.rowFilterDesc}` : ''}
                        </span>
                    </>
                )}
            </div>
        );
    };

    const renderProgress = (carrierKey) => {
        const state = billData[carrierKey];
        if (!state.isProcessing && !state.isDone && !state.errorMsg) return null;
        return (
            <div className="rtx-progress-wrap">
                <div className="rtx-progress-head">
                    <span>{state.errorMsg ? '집계 실패' : state.progressLabel}</span>
                    <b style={{fontSize: '15px', color: state.errorMsg ? '#e05e6d' : '#FFAF34'}}>{state.progress}%</b>
                </div>
                <div className="rtx-progress-bar">
                    <div className={`rtx-progress-fill${state.errorMsg ? ' is-error' : (state.isDone ? ' is-done' : '')}`}
                         style={{width: `${state.progress}%`}}/>
                </div>
            </div>
        );
    };

    const renderSummary = (carrierKey) => {
        const carrier = BILL_CARRIERS[carrierKey];
        const s = billData[carrierKey].summary;
        if (!s) return null;

        return (
            <>
                <div className="rtx-section-title">집계 근거 요약</div>
                <div className="rtx-summary-grid">
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">엑셀 전체 행수</div>
                        <div className="rtx-sc-value">{formatNum(s.lastRow)} 행</div>
                        <div className="rtx-sc-sub">시트 : {s.sheetName}</div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">운송장 인식 행수</div>
                        <div className="rtx-sc-value">{formatNum(s.matchedRowCount)} 행</div>
                        <div className="rtx-sc-sub">
                            {carrier.waybillCol}열 값 존재 {formatNum(s.valuedRowCount)}행 중 패턴불일치 {formatNum(s.skippedRowCount)}행 제외
                        </div>
                    </div>
                    {carrier.rowFilter && (
                        <div className="rtx-summary-card" style={{borderColor: '#e05e6d'}}>
                            <div className="rtx-sc-label">{carrier.rowFilterDesc}</div>
                            <div className="rtx-sc-value">{formatNum(s.aggregatedRowCount)} 행 집계</div>
                            <div className="rtx-sc-sub">
                                제외 {formatNum(s.filteredOutRowCount)}행 / {formatWon(s.filteredOutAmount)}
                                {s.onlyFilteredWaybillCount > 0 &&
                                    ` · 제외전용 운송장 ${formatNum(s.onlyFilteredWaybillCount)}건`}
                            </div>
                        </div>
                    )}
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">고유 운송장번호</div>
                        <div className="rtx-sc-value" style={{color: '#FFAF34'}}>{formatNum(s.uniqueWaybillCount)} 건</div>
                        <div className="rtx-sc-sub">중복 {formatNum(s.duplicatedRowCount)}행은 운송장별로 합산</div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">DB 매칭 / 미매칭</div>
                        <div className="rtx-sc-value">
                            {formatNum(s.dbMatchedCount)} 건
                            <span style={{fontSize: '13px', color: '#e05e6d', marginLeft: '8px'}}>
                                / 오류 {formatNum(s.dbUnMatchedCount)}건
                            </span>
                        </div>
                        <div className="rtx-sc-sub">조회서버 : {s.dataSource === 'PROD' ? '운영(btorage.com)' : '개발(btorage-dev.com)'}</div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">고객총결제 합계</div>
                        <div className="rtx-sc-value">{formatWon(s.sumTtShipExpense)}</div>
                        <div className="rtx-sc-sub">payInfoDTO.ttShipExpense 합</div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">예상송료 합계</div>
                        <div className="rtx-sc-value">{formatWon(s.sumShipPrice)}</div>
                        <div className="rtx-sc-sub">shipInfoDTO.shipPrice 합</div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">실청구액 합계 ({carrier.amountCol}열)</div>
                        <div className="rtx-sc-value" style={{color: carrier.themeColor}}>{formatWon(s.sumBilledAmount)}</div>
                        <div className="rtx-sc-sub">
                            + 오류분 {formatWon(s.sumErrorBilledAmount)} = {formatWon(s.excelTotalAmount)}&nbsp;
                            {s.amountVerified
                                ? <span className="rtx-verify-ok">✔ 원본일치</span>
                                : <span className="rtx-verify-ng">✖ 불일치</span>}
                        </div>
                    </div>
                    <div className="rtx-summary-card">
                        <div className="rtx-sc-label">수익 합계</div>
                        <div className={`rtx-sc-value ${s.sumProfit < 0 ? 'rtx-profit-minus' : 'rtx-profit-plus'}`}>
                            {formatWon(s.sumProfit)}
                        </div>
                        <div className="rtx-sc-sub">고객총결제 합 − 실청구액 합</div>
                    </div>
                </div>

                <div className="rtx-formula-box">
                    <div>▶ <b>실청구액</b> = 청구서 <b>{carrier.waybillCol}열</b> 운송장번호가 동일한 <b>모든 행</b>의 <b>{carrier.amountCol}열</b> 금액 총합
                        {carrier.rowFilter && <span> &nbsp;(단, <b>{carrier.rowFilterDesc}</b>)</span>}
                    </div>
                    <div>▶ <b>고객총결제</b> = payInfoDTO.ttShipExpense &nbsp;/&nbsp; <b>예상송료</b> = shipInfoDTO.shipPrice</div>
                    <div>▶ <b>수익</b> = 고객총결제 − 실청구액 &nbsp;→&nbsp;
                        {formatWon(s.sumTtShipExpense)} − {formatWon(s.sumBilledAmount)} = <b>{formatWon(s.sumProfit)}</b></div>
                    <div>▶ <b>데이터 조회</b> = 중복 제거된 운송장번호 {formatNum(s.uniqueWaybillCount)}건으로 ShipInfo.outWayNum 매칭 →
                        연결된 Orderdt / PayInfo 를 단일쿼리(DTO 직접조회)로 조인조회
                    </div>
                    <div>▶ <b>집계목록오류</b> = 청구서에는 운송장번호·실청구액이 있으나 DB에서 찾지 못한 {formatNum(s.dbUnMatchedCount)}건
                        (예상송료 0 고정 / 실청구액 합 {formatWon(s.sumErrorBilledAmount)})
                    </div>
                    {s.skippedRowCount > 0 && (
                        <div style={{color: '#9a9a9a', marginTop: '6px'}}>
                            ▶ 제외행 예시 : {s.skippedSamples.join(' / ')}
                            {s.skippedRowCount > s.skippedSamples.length ? ` 외 ${formatNum(s.skippedRowCount - s.skippedSamples.length)}건` : ''}
                            &nbsp;(헤더/합계행 등 운송장 패턴 불일치 행)
                        </div>
                    )}
                </div>
            </>
        );
    };

    // 집계목록 (DB 매칭 성공)
    const renderMainTable = (carrierKey) => {
        const carrier = BILL_CARRIERS[carrierKey];
        const state = billData[carrierKey];
        if (!state.summary) return null;
        const s = state.summary;

        return (
            <>
                <div className="rtx-toolbar" style={{marginTop: '26px'}}>
                    <div className="rtx-section-title" style={{margin: 0}}>
                        {carrier.label} 집계목록 ({formatNum(state.rows.length)}건)
                        <span style={{fontSize: '12px', fontWeight: 400, color: '#9a9a9a', marginLeft: '6px'}}>
                            · 행을 클릭하면 실청구액 합산근거가 펼쳐집니다 · 20행 이후는 스크롤로 확인
                        </span>
                    </div>
                    <button className="admin-btn-A wd-130 ht-32 font-s-13 btn-click-event"
                            onClick={() => downloadBillExcel(carrierKey)}>엑셀 다운로드
                    </button>
                </div>

                {state.rows.length === 0 ? (
                    <div className="rtx-empty-box">DB와 매칭된 집계목록 데이터가 없습니다.</div>
                ) : (
                    <div className="rtx-table-scroll">
                        <table className="rtx-table">
                            <thead>
                            <tr>
                                <th style={{width: '55px'}}>No</th>
                                <th style={{width: '210px'}}>주문번호</th>
                                <th style={{width: '120px'}}>성함</th>
                                <th style={{width: '95px'}}>배송사</th>
                                <th style={{width: '180px'}}>운송장번호</th>
                                <th className="rtx-num" style={{width: '130px'}}>고객총결제</th>
                                <th className="rtx-num" style={{width: '130px'}}>예상송료</th>
                                <th className="rtx-num" style={{width: '130px'}}>실청구액</th>
                                <th className="rtx-num" style={{width: '130px'}}>수익</th>
                            </tr>
                            </thead>
                            <tbody>
                            {state.rows.map((row) => {
                                const rowKey = `${carrierKey}-M-${row.waybill}`;
                                const isExpanded = expandedRowKey === rowKey;
                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr style={{cursor: 'pointer'}}
                                            onClick={() => setExpandedRowKey(isExpanded ? '' : rowKey)}>
                                            <td>{row.no}</td>
                                            <td>{row.orderBoxNo}</td>
                                            <td>{row.regNm || '-'}</td>
                                            <td>{row.shipcoLabel || '-'}</td>
                                            <td>
                                                {row.waybill}
                                                {row.billRowCount > 1 &&
                                                    <span className="rtx-badge-dup">{row.billRowCount}행 합산</span>}
                                            </td>
                                            <td className="rtx-num">{formatNum(row.ttShipExpense)}</td>
                                            <td className="rtx-num">{formatNum(row.shipPrice)}</td>
                                            <td className="rtx-num">{formatNum(row.billedAmount)}</td>
                                            <td className={`rtx-num ${row.profit < 0 ? 'rtx-profit-minus' : 'rtx-profit-plus'}`}>
                                                {formatNum(row.profit)}
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={9} style={{textAlign: 'left', padding: '12px 18px', background: 'rgba(255,175,52,0.05)'}}>
                                                    <div style={{fontSize: '12px', lineHeight: '21px'}}>
                                                        <b>실청구액 합산근거</b> — 청구서 {carrier.waybillCol}열 운송장 <b>{row.waybill}</b> 해당 행 {row.billRowCount}건
                                                        <div style={{marginTop: '4px'}}>
                                                            {row.billRows.map((r, i) => (
                                                                <span key={r.rowNum} style={{marginRight: '10px'}}>
                                                                    {i > 0 && '+ '}{r.rowNum}행[{r.type}] {formatNum(r.amount)}
                                                                </span>
                                                            ))}
                                                            <b style={{marginLeft: '6px'}}>= {formatWon(row.billedAmount)}</b>
                                                        </div>
                                                        {carrier.typeCol && (
                                                            <div style={{marginTop: '6px', color: '#9a9a9a'}}>
                                                                {carrier.typeColLabel}별 소계 :&nbsp;
                                                                {Object.entries(row.typeBreakdown).map(([type, amt]) => (
                                                                    <span key={type} style={{marginRight: '10px'}}>{type} {formatWon(amt)}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <div style={{marginTop: '6px'}}>
                                                            수익 = 고객총결제 {formatWon(row.ttShipExpense)} − 실청구액 {formatWon(row.billedAmount)} =&nbsp;
                                                            <b className={row.profit < 0 ? 'rtx-profit-minus' : 'rtx-profit-plus'}>{formatWon(row.profit)}</b>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colSpan={5}>합계 ({formatNum(state.rows.length)}건)</td>
                                <td className="rtx-num">{formatNum(s.sumTtShipExpense)}</td>
                                <td className="rtx-num">{formatNum(s.sumShipPrice)}</td>
                                <td className="rtx-num">{formatNum(s.sumBilledAmount)}</td>
                                <td className={`rtx-num ${s.sumProfit < 0 ? 'rtx-profit-minus' : 'rtx-profit-plus'}`}>
                                    {formatNum(s.sumProfit)}
                                </td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </>
        );
    };

    /* ── 부가세탭 : 업로드폼 카드 ── */
    const renderVatUploadCard = (form) => {
        const state = vatUploads[form.key] || {};
        const isDragging = vatDraggingKey === form.key;
        const log = state.logResult;

        return (
            <div key={form.key}
                 className={`rtx-upload-card${isDragging ? ' is-dragging' : ''}${state.done ? ' is-done' : ''}`}
                 onClick={() => handleVatUploadClick(form.key)}
                 onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setVatDraggingKey(form.key); }}
                 onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setVatDraggingKey(form.key); }}
                 onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setVatDraggingKey(''); }}
                 onDrop={(e) => handleVatDrop(form.key, e)}>

                <div className="rtx-upload-card-title">
                    {form.label}
                    {state.done && (
                        <button className="rtx-upload-reset" onClick={(e) => handleVatReset(form.key, e)}>초기화</button>
                    )}
                </div>

                {state.loading ? (
                    <div className="rtx-upload-body rtx-upload-empty">
                        <div className="orangeSpinner"/>
                        <p className="rtx-dropzone-desc m-t-10">
                            {state.parsingOriginal
                                ? '원본 서식/병합 분석중...'
                                : '파일 분석중...'}
                        </p>
                        {state.parsingOriginal && (
                            <p className="rtx-upload-sub" style={{textAlign: 'center'}}>
                                병합이 많은 파일은 수십초가 걸릴 수 있습니다
                            </p>
                        )}
                    </div>
                ) : state.done ? (
                    <div className="rtx-upload-body">
                        {form.purchaseLog ? (
                            <>
                                <div className="rtx-upload-amount-row">
                                    <span>오프라인 수수료</span><b>{formatWon(log?.offlineFee)}</b>
                                </div>
                                <div className="rtx-upload-amount-row">
                                    <span>이체대행 수수료</span><b>{formatWon(log?.transferFee)}</b>
                                </div>
                                <div className="rtx-upload-amount-row">
                                    <span>국내배송 수수료</span><b>{formatWon(log?.domesticFee)}</b>
                                </div>
                                <div className="rtx-upload-sub">
                                    국내배송 실결제 {formatWon(log?.domesticPaySum)} (전체취소 {formatNum(log?.domesticExcludedCount)}건 제외)<br/>
                                    이체대행 실결제 {formatWon(log?.transferPaySum)} (취소 {formatNum(log?.transferExcludedCount)}건 제외)
                                </div>
                                <div className="rtx-log-sheets" onClick={(e) => e.stopPropagation()}>
                                    <div className="rtx-upload-sub" style={{marginBottom: '4px'}}>
                                        집계 탭 선택 ({formatNum(state.selectedSheets?.length)}/{formatNum(state.allSheets?.length)})
                                        {state.autoSelectedByPeriod && <span style={{color: '#FFAF34'}}> · 조회기간 자동선택</span>}
                                    </div>
                                    {(state.allSheets || []).map((name) => (
                                        <label key={name} className="rtx-log-sheet-chip">
                                            <input type="checkbox"
                                                   checked={(state.selectedSheets || []).includes(name)}
                                                   onChange={() => handleToggleLogSheet(name)}/>
                                            {name}
                                        </label>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="rtx-upload-amount-row">
                                    <span>총 금액 ({form.amountColDesc || `${form.amountCol}열`})</span>
                                    <b style={{color: '#4B89DC'}}>{formatWon(state.totalAmount)}</b>
                                </div>
                                <div className="rtx-upload-sub">
                                    시트 {state.sheetName} · 집계행 {formatNum(state.valuedRowCount)} / 전체 {formatNum(state.lastRow)}행
                                    {state.firstVisibleRow > 0 && ` (${formatNum(state.firstVisibleRow)}~${formatNum(state.lastVisibleRow)}행)`}
                                    {state.hiddenRowCount > 0 && (
                                        <><br/><span style={{color: '#b07400'}}>
                                            숨김행 {formatNum(state.hiddenRowCount)}건 / {formatWon(state.hiddenAmount)} 은 집계 제외
                                        </span></>
                                    )}
                                </div>
                                {form.carrierKey && state.billParsed && (
                                    <div className="rtx-upload-sub">
                                        고유 운송장 {formatNum(state.billParsed.uniqueWaybillCount)}건 ·
                                        매칭 {formatNum(state.matchedCount)}건 ·
                                        <span style={{color: '#e05e6d'}}> 오류 {formatNum(state.errorRows?.length)}건</span>
                                        {state.billParsed.filteredOutRowCount > 0 && (
                                            <><br/>※ 운송장 집계기준 : {BILL_CARRIERS[form.carrierKey].rowFilterDesc}
                                                (제외 {formatNum(state.billParsed.filteredOutRowCount)}행 / {formatWon(state.billParsed.filteredOutAmount)})</>
                                        )}
                                    </div>
                                )}
                                {form.postPayback && state.payback && (
                                    <div className="rtx-upload-sub">
                                        우체국 페이백 {formatNum(state.payback.rows.length)}건 /
                                        <b style={{color: '#16a34a'}}> {formatWon(Math.abs(state.payback.total))}</b>
                                        <br/>(우정사업본부 {formatNum(state.payback.postRowCount)}행 → 음수 {formatNum(state.payback.minusRowCount)}행 → 유일금액 {formatNum(state.payback.rows.length)}행)
                                    </div>
                                )}
                            </>
                        )}
                        <div className="rtx-upload-filename">{state.fileName}</div>
                    </div>
                ) : (
                    <div className="rtx-upload-body rtx-upload-empty">
                        <Lottie animationData={taxExcel} loop autoplay className="opa06 wd-70 ht-70"/>
                        <p className="rtx-upload-guide">클릭 및 드래그로<br/>파일을 업로드하세요</p>
                        {state.errorMsg && <p className="rtx-upload-err">{state.errorMsg}</p>}
                    </div>
                )}
            </div>
        );
    };

    /* ── 부가세탭 : 배송사별 청구오류폼 ── */
    const renderVatBillErrorForm = (formKey) => {
        const form = VAT_UPLOAD_FORMS.find((f) => f.key === formKey);
        const state = vatUploads[formKey];
        if (!form || !state?.done || !state.errorRows) return null;
        const carrier = BILL_CARRIERS[form.carrierKey];
        const useRefCol = !!carrier.refOrderNoCol;

        return (
            <div key={formKey} style={{marginTop: '18px'}}>
                <div className="rtx-section-title" style={{color: '#e05e6d', marginTop: 0}}>
                    {carrier.label} 청구오류 ({formatNum(state.errorRows.length)}건 / {formatWon(state.errorTotal)})
                    <span style={{fontSize: '12px', fontWeight: 400, color: '#9a9a9a', marginLeft: '6px'}}>
                        · 청구서에는 운송장·운송료가 있으나 조회된 해외배송료 데이터의 운송장번호와 매칭되지 않음
                        ({state.comparedAgainst === 'FILTERED' ? '필터된 집계대상' : '조회 전체'} 기준)
                    </span>
                </div>
                {state.errorRows.length === 0 ? (
                    <div className="rtx-empty-box" style={{padding: '26px'}}>청구오류 데이터가 없습니다.</div>
                ) : (
                    <div className="rtx-table-scroll" style={{maxHeight: '440px'}}>
                        <table className="rtx-table">
                            <thead>
                            <tr>
                                <th style={{width: '55px'}}>No</th>
                                <th style={{width: '210px'}}>주문번호({carrier.orderNoCol}열)</th>
                                {useRefCol && <th style={{width: '200px'}}>{carrier.refOrderNoLabel}</th>}
                                <th style={{width: '190px'}}>운송장번호({carrier.waybillCol}열)</th>
                                <th className="rtx-num" style={{width: '110px'}}>예상송료</th>
                                <th className="rtx-num" style={{width: '140px'}}>실청구액</th>
                            </tr>
                            </thead>
                            <tbody>
                            {state.errorRows.map((row) => (
                                <tr key={row.waybill} className="is-unmatched">
                                    <td>{row.no}</td>
                                    <td>{row.orderNumber}</td>
                                    {useRefCol && <td>{row.refOrderNumber || '-'}</td>}
                                    <td>
                                        {row.waybill}
                                        {row.billRowCount > 1 && <span className="rtx-badge-dup">{row.billRowCount}행 합산</span>}
                                    </td>
                                    <td className="rtx-num">0</td>
                                    <td className="rtx-num">{formatNum(row.billedAmount)}</td>
                                </tr>
                            ))}
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colSpan={useRefCol ? 4 : 3}>합계 ({formatNum(state.errorRows.length)}건)</td>
                                <td className="rtx-num">0</td>
                                <td className="rtx-num">{formatNum(state.errorTotal)}</td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    /* ── 부가세탭 : 수동입력폼 1개 블록 ── */
    const renderManualBilledForm = (title, customerSum, value, setValue, profit) => (
        <div className="rtx-summary-card">
            <div className="rtx-sc-label">{title}</div>
            <div className="rtx-manual-row">
                <span>고객총결제 (자동)</span><b>{formatWon(customerSum)}</b>
            </div>
            <div className="rtx-manual-row">
                <span>총 청구금액 (입력)</span>
                <input className="rtx-manual-input" type="text" inputMode="numeric" placeholder="0"
                       value={value ? Number(String(value).replace(/[^0-9]/g, '') || 0).toLocaleString() : ''}
                       onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}/>
            </div>
            <div className="rtx-manual-row">
                <span>총 수익 (자동)</span>
                <b className={profit < 0 ? 'rtx-profit-minus' : 'rtx-profit-plus'}>{formatWon(profit)}</b>
            </div>
        </div>
    );

    // 집계목록오류 (청구서에는 있으나 DB 미매칭)
    const renderErrorTable = (carrierKey) => {
        const carrier = BILL_CARRIERS[carrierKey];
        const state = billData[carrierKey];
        if (!state.summary) return null;
        const s = state.summary;
        const useRefCol = !!carrier.refOrderNoCol;
        const colSpan = useRefCol ? 6 : 5;

        return (
            <>
                <div className="rtx-section-title" style={{marginTop: '30px', color: '#e05e6d'}}>
                    {carrier.label} 집계목록오류 ({formatNum(state.errorRows.length)}건)
                    <span style={{fontSize: '12px', fontWeight: 400, color: '#9a9a9a', marginLeft: '6px'}}>
                        · 청구서에는 운송장번호·실청구액이 있으나 데이터베이스에서 찾지 못한 건
                    </span>
                </div>

                {state.errorRows.length === 0 ? (
                    <div className="rtx-empty-box" style={{padding: '30px'}}>
                        집계목록오류 데이터가 없습니다. (청구서의 모든 운송장이 데이터베이스와 매칭되었습니다)
                    </div>
                ) : (
                    <div className="rtx-table-scroll">
                        <table className="rtx-table">
                            <thead>
                            <tr>
                                <th style={{width: '55px'}}>No</th>
                                <th style={{width: '210px'}}>주문번호({carrier.orderNoCol}열)</th>
                                {useRefCol && <th style={{width: '210px'}}>{carrier.refOrderNoLabel}</th>}
                                <th style={{width: '190px'}}>운송장번호({carrier.waybillCol}열)</th>
                                <th className="rtx-num" style={{width: '120px'}}>예상송료</th>
                                <th className="rtx-num" style={{width: '140px'}}>실청구액</th>
                            </tr>
                            </thead>
                            <tbody>
                            {state.errorRows.map((row) => {
                                const rowKey = `${carrierKey}-E-${row.waybill}`;
                                const isExpanded = expandedRowKey === rowKey;
                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr className="is-unmatched" style={{cursor: 'pointer'}}
                                            onClick={() => setExpandedRowKey(isExpanded ? '' : rowKey)}>
                                            <td>{row.no}</td>
                                            <td>{row.orderNumber}</td>
                                            {useRefCol && <td>{row.refOrderNumber || '-'}</td>}
                                            <td>
                                                {row.waybill}
                                                {row.billRowCount > 1 &&
                                                    <span className="rtx-badge-dup">{row.billRowCount}행 합산</span>}
                                            </td>
                                            <td className="rtx-num">0</td>
                                            <td className="rtx-num">{formatNum(row.billedAmount)}</td>
                                        </tr>
                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={colSpan} style={{textAlign: 'left', padding: '12px 18px', background: 'rgba(224,94,109,0.05)'}}>
                                                    <div style={{fontSize: '12px', lineHeight: '21px'}}>
                                                        <b>실청구액 합산근거</b> — 청구서 {carrier.waybillCol}열 운송장 <b>{row.waybill}</b> 해당 행 {row.billRowCount}건
                                                        <div style={{marginTop: '4px'}}>
                                                            {row.billRows.map((r, i) => (
                                                                <span key={r.rowNum} style={{marginRight: '10px'}}>
                                                                    {i > 0 && '+ '}{r.rowNum}행[{r.type}] {formatNum(r.amount)}
                                                                </span>
                                                            ))}
                                                            <b style={{marginLeft: '6px'}}>= {formatWon(row.billedAmount)}</b>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                            <tfoot>
                            <tr>
                                <td colSpan={colSpan - 2}>합계 ({formatNum(state.errorRows.length)}건)</td>
                                <td className="rtx-num">0</td>
                                <td className="rtx-num">{formatNum(s.sumErrorBilledAmount)}</td>
                            </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </>
        );
    };
    /* endregion */

    return (
        <div className="AdminPageSetPg font-t-cont">
            <div className="admin-work-title">
                <img className="wd-35 ht-35" alt="" src={maCateImg}/>
                <p className="p-t-6">세금신고관리(신규)</p>
            </div>

            <div className="admin-wrap">
                <div className="admin-wrap-card">

                    {/* 상위 탭 : 청구서 / 부가세 / 법인세 */}
                    <div className="rtx-tabs font-t-title">
                        <div className={`rtx-tab${mainTab === 'BILL' ? ' is-active' : ''}`}
                             onClick={() => setMainTab('BILL')}>청구서
                        </div>
                        <div className={`rtx-tab${mainTab === 'VAT' ? ' is-active' : ''}`}
                             onClick={() => setMainTab('VAT')}>부가세
                        </div>
                        <div className={`rtx-tab${mainTab === 'CT' ? ' is-active' : ''}`}
                             onClick={() => setMainTab('CT')}>법인세
                        </div>
                    </div>

                    {/* ================= 청구서 탭 ================= */}
                    {mainTab === 'BILL' && (
                        <div>
                            {/* 배송사 하위탭 + 조회서버 토글 */}
                            <div className="rtx-toolbar">
                                <div className="rtx-subtabs">
                                    {CARRIER_ORDER.map((key) => {
                                        const carrier = BILL_CARRIERS[key];
                                        return (
                                            <div key={key}
                                                 className={`rtx-subtab${billCarrierTab === key ? ' is-active' : ''}`}
                                                 onClick={() => {
                                                     setBillCarrierTab(key);
                                                     setExpandedRowKey('');
                                                 }}>
                                                {carrier.label}
                                                {carrierBadge[key] > 0 &&
                                                    <span className="rtx-subtab-badge">{formatNum(carrierBadge[key])}</span>}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ⚠ 임시 : 운영 데이터 검증용 조회서버 토글 (검증완료 후 제거예정) */}
                                <div className="display-flex align-center" style={{gap: '8px'}}>
                                    <span style={{fontSize: '12px', color: '#9a9a9a'}}>데이터 조회서버</span>
                                    <div className="rtx-server-toggle">
                                        <button className={billDataSource === 'DEV' ? 'is-active' : ''}
                                                onClick={() => setBillDataSource('DEV')}>개발
                                        </button>
                                        <button className={billDataSource === 'PROD' ? 'is-active' : ''}
                                                onClick={() => setBillDataSource('PROD')}>운영
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* 숨김 파일 input (배송사별 개별 ref) */}
                            <input type="file" accept=".xlsx,.xls" ref={emsInputRef} hidden
                                   onChange={(e) => handleFileInputChange('EMS', e)}/>
                            <input type="file" accept=".xlsx,.xls" ref={fedexInputRef} hidden
                                   onChange={(e) => handleFileInputChange('FEDEX', e)}/>
                            <input type="file" accept=".xlsx,.xls" ref={upsInputRef} hidden
                                   onChange={(e) => handleFileInputChange('UPS', e)}/>

                            {renderDropzone(billCarrierTab)}
                            {renderProgress(billCarrierTab)}

                            {currentBill.errorMsg && <div className="rtx-error-box">{currentBill.errorMsg}</div>}

                            {renderSummary(billCarrierTab)}
                            {renderMainTable(billCarrierTab)}
                            {renderErrorTable(billCarrierTab)}

                            {!currentBill.isDone && !currentBill.isProcessing && !currentBill.errorMsg && (
                                <div className="rtx-empty-box">
                                    {currentCarrier.fullLabel} 엑셀을 업로드하면 운송장번호 기준으로 데이터베이스와 대조하여
                                    집계목록 / 집계목록오류가 출력됩니다.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ================= 부가세 탭 ================= */}
                    {mainTab === 'VAT' && (
                        <div>
                            {/* 날짜선택 + 분기버튼 + 데이터조회 */}
                            <div className="rtx-toolbar">
                                <div className="display-flex align-center" style={{gap: '10px', flexWrap: 'wrap'}}>
                                    <AdminYmdEvent startDate={vatStartDate} endDate={vatEndDate}
                                                   startHandle={vatStartHandle} endHandle={vatEndHandle}/>
                                    <div className="display-flex align-center" style={{gap: '5px'}}>
                                        {[1, 2, 3, 4].map((q) => (
                                            <button key={q}
                                                    className={`rtx-quarter-btn${vatQuarter === q ? ' is-active' : ''}`}
                                                    onClick={() => handleQuarterClick(q)}>{q}분기
                                            </button>
                                        ))}
                                    </div>
                                    <button className="admin-btn-A wd-110 ht-32 font-s-13 btn-click-event"
                                            disabled={vatLoading}
                                            onClick={handleSelectVatData}>
                                        {vatLoading ? '조회중...' : '데이터조회'}
                                    </button>
                                    {vatFilterDone && vatPt1All.length > 0 && (
                                        <button className="admin-btn-B wd-110 ht-32 font-s-13"
                                                onClick={() => setVatFilterShow(true)}>필터 다시열기
                                        </button>
                                    )}
                                </div>

                                {/* ⚠ 임시 : 청구서탭과 동일한 조회서버 토글 공유 (검증완료 후 제거예정) */}
                                <div className="display-flex align-center" style={{gap: '8px'}}>
                                    <span style={{fontSize: '12px', color: '#9a9a9a'}}>데이터 조회서버</span>
                                    <div className="rtx-server-toggle">
                                        <button className={billDataSource === 'DEV' ? 'is-active' : ''}
                                                onClick={() => setBillDataSource('DEV')}>개발
                                        </button>
                                        <button className={billDataSource === 'PROD' ? 'is-active' : ''}
                                                onClick={() => setBillDataSource('PROD')}>운영
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {vatErrorMsg && <div className="rtx-error-box">{vatErrorMsg}</div>}

                            {/* 조회결과 개요 */}
                            {vatPt1All.length > 0 && (
                                <div className="rtx-summary-grid" style={{marginTop: '18px'}}>
                                    <div className="rtx-summary-card">
                                        <div className="rtx-sc-label">조회기간</div>
                                        <div className="rtx-sc-value" style={{fontSize: '15px'}}>{vatStartDate} ~ {vatEndDate}</div>
                                        <div className="rtx-sc-sub">payInfo.shipDate(PT1) / payDate·modiDate(PT2) 기준</div>
                                    </div>
                                    <div className="rtx-summary-card">
                                        <div className="rtx-sc-label">PT1 출고완료 조회</div>
                                        <div className="rtx-sc-value">{formatNum(vatPt1All.length)} 건</div>
                                        <div className="rtx-sc-sub">
                                            필터 선택 {formatNum(vatPt1Filtered.length)}건 / 제외 {formatNum(vatPt1All.length - vatPt1Filtered.length)}건
                                        </div>
                                    </div>
                                    <div className="rtx-summary-card">
                                        <div className="rtx-sc-label">PT2 기타비용 조회</div>
                                        <div className="rtx-sc-value">{formatNum(vatPt2All.length)} 건</div>
                                        <div className="rtx-sc-sub">기타비용은 필터대상이 아니며 전건 집계</div>
                                    </div>
                                    <div className="rtx-summary-card">
                                        <div className="rtx-sc-label">적립금 사용/취소 내역</div>
                                        <div className="rtx-sc-value">{formatNum(vatUsePointList.length)} 건</div>
                                        <div className="rtx-sc-sub">type 0(취소)+1(사용) / ptPrice ≠ 0</div>
                                    </div>
                                </div>
                            )}

                            {/* 필터완료된 집계대상 목록 */}
                            {vatFilterDone && (
                                <>
                                    <div className="rtx-section-title" style={{marginTop: '26px'}}>
                                        집계대상 데이터 ({formatNum(vatPt1Filtered.length)}건)
                                        <span style={{fontSize: '12px', fontWeight: 400, color: '#9a9a9a', marginLeft: '6px'}}>
                                            · 필터에서 제외된 데이터는 이후 모든 세무/비용 계산에 사용되지 않습니다
                                        </span>
                                    </div>

                                    {/* 배송사별 탭버튼 + 검색폼 */}
                                    <div className="rtx-subtabs" style={{flexWrap: 'wrap', gap: '6px'}}>
                                        {vatListShipcoTabs.map((code) => {
                                            const label = code === 'ALL' ? '전체' : convertToShipco(code, code);
                                            const count = code === 'ALL'
                                                ? vatPt1Filtered.length
                                                : vatPt1Filtered.filter((i) => i.dvType === code).length;
                                            return (
                                                <div key={code}
                                                     className={`rtx-subtab${vatListShipcoTab === code ? ' is-active' : ''}`}
                                                     style={{minWidth: 'auto', height: '32px', padding: '0 14px', fontSize: '13px'}}
                                                     onClick={() => setVatListShipcoTab(code)}>
                                                    {label}<span className="rtx-subtab-badge">{formatNum(count)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="display-flex align-center" style={{gap: '10px', margin: '0 0 12px'}}>
                                        <input className="admin-input-line ht-34 font-s-13 p-l-10"
                                               style={{flex: 1, maxWidth: '560px', borderRadius: '8px', border: '1px solid #dcdcdc'}}
                                               type="text"
                                               placeholder="박스번호 / 성함 / 배송사 / 총배송료(숫자만) / 운송장번호 검색"
                                               value={vatListSearch}
                                               onChange={(e) => setVatListSearch(e.target.value)}/>
                                        {vatListSearch && (
                                            <button className="admin-btn-B wd-70 ht-32 font-s-12"
                                                    onClick={() => setVatListSearch('')}>초기화
                                            </button>
                                        )}
                                        <span style={{fontSize: '12px', color: '#9a9a9a'}}>
                                            {formatNum(vatVisibleList.length)} / {formatNum(vatPt1Filtered.length)}건 표시
                                        </span>
                                    </div>

                                    {/* 10~15개만 보이고 나머지는 드래그(스크롤)로 확인 */}
                                    <div className="rtx-table-scroll" style={{maxHeight: '620px'}}>
                                        <table className="rtx-table">
                                            <thead>
                                            <tr>
                                                <th style={{width: '60px'}}>No</th>
                                                <th style={{width: '260px'}}>박스번호</th>
                                                <th style={{width: '140px'}}>성함</th>
                                                <th style={{width: '110px'}}>배송사</th>
                                                <th className="rtx-num" style={{width: '150px'}}>총배송료</th>
                                                <th style={{width: '200px'}}>운송장번호</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {vatVisibleList.map((item, index) => (
                                                <tr key={item.paySeqNum || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{`${item.orderNumber || ''}-${item.boxNum || ''}`}</td>
                                                    <td>{item.regNm || '-'}</td>
                                                    <td>{convertToShipco(item.dvType, item.shipco)}</td>
                                                    <td className="rtx-num">{formatNum(item.ttShipExpense)}</td>
                                                    <td>{item.outWayNum || '-'}</td>
                                                </tr>
                                            ))}
                                            {vatVisibleList.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} style={{padding: '40px 0', color: '#9a9a9a'}}>
                                                        조건에 맞는 데이터가 없습니다.
                                                    </td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* 사항3 집계결과 */}
                            {vatAgg && (
                                <>
                                    <div className="rtx-section-title">세무 집계값 (계산근거)</div>
                                    <div className="rtx-summary-grid">
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">EMS 배송료 합계 <span style={{color: '#9a9a9a'}}>(화면 참고용)</span></div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.ttEmsPrice)}</div>
                                            <div className="rtx-sc-sub">
                                                D02+D13+D15 배송료 {formatWon(vatAgg.emsShipSum)} + 기타(Add missing) {formatWon(vatAgg.emsEtcSum)}
                                                <br/>※ 최종파일 F11~F16 에는 미반영 (우체국 수익은 탭1 D24 · 탭2 페이백으로 별도집계)
                                            </div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">FedEx 배송료 합계</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.ttFedexPrice)}</div>
                                            <div className="rtx-sc-sub">
                                                배송료 {formatWon(vatAgg.fedexShipSum)} + 기타 {formatWon(vatAgg.fedexEtcSum)}
                                            </div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">UPS 배송료 합계</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.ttUpsPrice)}</div>
                                            <div className="rtx-sc-sub">
                                                배송료 {formatWon(vatAgg.upsShipSum)} + 기타 {formatWon(vatAgg.upsEtcSum)}
                                            </div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">국내배송(오프라인+국내배송폼) 택배비 수익</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.domesticFormShipFee)}</div>
                                            <div className="rtx-sc-sub">
                                                대상 {formatNum(vatAgg.domesticFormItemCount)}건 · Σ(택배비 − 송료)
                                            </div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">국내배송(배송대행폼) 택배비 수익</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.deliveryFormShipFee)}</div>
                                            <div className="rtx-sc-sub">
                                                대상 {formatNum(vatAgg.deliveryFormItemCount)}건 · Σ(택배비 − 송료)
                                            </div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">선편(D03) 고객총결제</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.seaCustomerSum)}</div>
                                            <div className="rtx-sc-sub">수동입력폼 자동계산값</div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">K-Packet(D13) 고객총결제</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.kPackCustomerSum)}</div>
                                            <div className="rtx-sc-sub">수동입력폼 자동계산값</div>
                                        </div>
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">S-Packet(D14) 고객총결제</div>
                                            <div className="rtx-sc-value">{formatWon(vatAgg.sPackCustomerSum)}</div>
                                            <div className="rtx-sc-sub">수동입력폼 자동계산값</div>
                                        </div>
                                    </div>

                                    {/* 수수료 3분류 */}
                                    <div className="rtx-section-title">각종 수수료 3분류 (etcFee 제외 / removeFee 포함)</div>
                                    <div className="rtx-table-scroll" style={{maxHeight: 'none'}}>
                                        <table className="rtx-table">
                                            <thead>
                                            <tr>
                                                <th className="rtx-left" style={{width: '300px'}}>구분</th>
                                                <th style={{width: '80px'}}>건수</th>
                                                <th className="rtx-num">배송대행</th>
                                                <th className="rtx-num">재포장</th>
                                                <th className="rtx-num">대행</th>
                                                <th className="rtx-num">압축팩</th>
                                                <th className="rtx-num">제거작업</th>
                                                <th className="rtx-num">수수료 소계</th>
                                                <th className="rtx-num">택배비 수익</th>
                                                <th className="rtx-num">최종값</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {[
                                                {label: '해외배송수수료 (shipco ≠ 국내배송)', cnt: vatAgg.overseasPt2Count, fee: vatAgg.overseasFee, ship: 0, total: vatAgg.overseasFeeTotal},
                                                {label: '국내배송(오프라인+국내배송폼)수수료', cnt: vatAgg.domesticFormPt2Count, fee: vatAgg.domesticFormFee, ship: vatAgg.domesticFormShipFee, total: vatAgg.domesticFormFeeTotal},
                                                {label: '국내배송(배송대행폼)수수료', cnt: vatAgg.deliveryFormPt2Count, fee: vatAgg.deliveryFormFee, ship: vatAgg.deliveryFormShipFee, total: vatAgg.deliveryFormFeeTotal},
                                            ].map((row) => (
                                                <tr key={row.label}>
                                                    <td className="rtx-left">{row.label}</td>
                                                    <td>{formatNum(row.cnt)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.deliveryFee)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.repackFee)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.agencyFee)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.comprFee)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.removeFee)}</td>
                                                    <td className="rtx-num">{formatNum(row.fee.total)}</td>
                                                    <td className="rtx-num">{row.ship ? formatNum(row.ship) : '-'}</td>
                                                    <td className="rtx-num rtx-profit-plus">{formatNum(row.total)}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="rtx-formula-box">
                                        <div>▶ <b>해외배송수수료</b> = shipco ≠ "국내배송" 인 PT2 의 5종 수수료 총합 = <b>{formatWon(vatAgg.overseasFeeTotal)}</b></div>
                                        <div>▶ <b>국내배송(오프라인+국내배송폼)</b> = 택배비수익 {formatWon(vatAgg.domesticFormShipFee)} + 수수료 {formatWon(vatAgg.domesticFormFee.total)} = <b>{formatWon(vatAgg.domesticFormFeeTotal)}</b>
                                            &nbsp;<span style={{color: '#9a9a9a'}}>(주문서에 P02:오프라인 또는 P04:국내배송 상품 존재)</span></div>
                                        {vatUploads.ORDER_LOG?.logResult && (
                                            <div>▶ <b>탭1 F14 최종값</b> = 위 {formatWon(vatAgg.domesticFormFeeTotal)} + 구매일지 국내배송 수수료 {formatWon(vatUploads.ORDER_LOG.logResult.domesticFee)}
                                                &nbsp;= <b>{formatWon((vatAgg.domesticFormFeeTotal || 0) + (vatUploads.ORDER_LOG.logResult.domesticFee || 0))}</b></div>
                                        )}
                                        <div>▶ <b>국내배송(배송대행폼)</b> = 택배비수익 {formatWon(vatAgg.deliveryFormShipFee)} + 수수료 {formatWon(vatAgg.deliveryFormFee.total)} = <b>{formatWon(vatAgg.deliveryFormFeeTotal)}</b>
                                            &nbsp;<span style={{color: '#9a9a9a'}}>(주문서에 오직 P05:배송대행 상품만 존재)</span></div>
                                                            <div style={{color: '#9a9a9a', marginTop: '6px'}}>▶ 집계시각 : {vatAgg.aggregatedAt} · 집계대상 PT1 {formatNum(vatAgg.pt1Count)}건 / PT2 {formatNum(vatAgg.pt2Count)}건</div>
                                    </div>

                                    {/* ── 업로드폼 UI (7종) ── */}
                                    <div className="rtx-section-title">업로드폼 (엑셀 업로드)</div>
                                    <input type="file" accept=".xlsx,.xls" ref={vatFileInputRef} hidden
                                           onChange={handleVatFileInputChange}/>
                                    <div className="rtx-upload-grid">
                                        {VAT_UPLOAD_FORMS.map((form) => renderVatUploadCard(form))}
                                    </div>

                                    {/* ── 배송사별 청구오류폼 ── */}
                                    {['EMS_BILL', 'FEDEX_BILL', 'UPS_BILL'].some((k) => vatUploads[k]?.done) && (
                                        <>
                                            <div className="rtx-section-title">배송사별 청구오류폼</div>
                                            {['EMS_BILL', 'FEDEX_BILL', 'UPS_BILL'].map((k) => renderVatBillErrorForm(k))}
                                        </>
                                    )}

                                    {/* ── 수동입력폼 UI ── */}
                                    <div className="rtx-section-title">수동입력폼</div>
                                    <div className="rtx-summary-grid">
                                        {renderManualBilledForm('선편 총 청구금액 (dvType D03 / SEA)',
                                            vatAgg.seaCustomerSum, manualSeaBilled, setManualSeaBilled, manualSeaProfit)}
                                        {renderManualBilledForm('K-Packet 총 청구금액 (dvType D13)',
                                            vatAgg.kPackCustomerSum, manualKPackBilled, setManualKPackBilled, manualKPackProfit)}
                                        {renderManualBilledForm('S-Packet 총 청구금액 (dvType D14 / Small-Packet)',
                                            vatAgg.sPackCustomerSum, manualSPackBilled, setManualSPackBilled, manualSPackProfit)}
                                        <div className="rtx-summary-card">
                                            <div className="rtx-sc-label">Anell 총 수수료</div>
                                            <div className="rtx-manual-row">
                                                <span>총 수수료 (입력)</span>
                                                <input className="rtx-manual-input" type="text" inputMode="numeric" placeholder="0"
                                                       value={manualAnellFee ? Number(String(manualAnellFee).replace(/[^0-9]/g, '') || 0).toLocaleString() : ''}
                                                       onChange={(e) => setManualAnellFee(e.target.value.replace(/[^0-9]/g, ''))}/>
                                            </div>
                                            <div className="rtx-sc-sub" style={{marginTop: '10px'}}>
                                                탭1 F12 (해외배송수수료) 합산항목
                                            </div>
                                        </div>
                                    </div>

                                    {/* ── 최종데이터 생성 버튼 (사항5) ── */}
                                    <div className="display-flex align-center just-cont-center" style={{margin: '30px 0 10px', gap: '12px'}}>
                                        <button className="admin-btn-A ht-44 font-s-15 btn-click-event"
                                                style={{width: '260px', opacity: canGenerateFinalData && !finalGenLoading ? 1 : .45,
                                                    cursor: canGenerateFinalData && !finalGenLoading ? 'pointer' : 'not-allowed'}}
                                                disabled={!canGenerateFinalData || finalGenLoading}
                                                onClick={generateFinalTaxFile}>
                                            {finalGenLoading ? '생성중...' : '최종데이터 생성'}
                                        </button>
                                    </div>
                                    <div className="rtx-empty-box" style={{padding: '10px'}}>
                                        {canGenerateFinalData
                                            ? '데이터 조회/필터 및 업로드폼 7종이 모두 완료되었습니다. (수동입력폼은 0원/미입력이어도 생성 가능)'
                                            : `생성조건 : 데이터조회+필터완료 ${vatFilterDone ? '✔' : '✖'} · 업로드폼 ${formatNum(VAT_UPLOAD_FORMS.filter((f) => vatUploads[f.key]?.done).length)}/${VAT_UPLOAD_FORMS.length} 완료`}
                                    </div>

                                    {/* 생성결과 : 최종파일에 어떤 값이 어떻게 들어갔는지 육안검증용 */}
                                    {finalGenLog && (
                                        <>
                                            <div className="rtx-section-title">최종파일 생성결과 · 삽입값 검증</div>
                                            <div className="rtx-formula-box" style={{background: 'rgba(22,163,74,.06)', borderColor: '#9dd49f'}}>
                                                <div>✔ <b>{finalGenLog.fileName}</b> 생성완료 ({finalGenLog.generatedAt})</div>
                                                <div style={{color: '#9a9a9a', marginTop: '4px'}}>
                                                    탭 구성 : {finalGenLog.sheetNames.map((n, i) => `${i + 1}.${n}`).join('  ·  ')}
                                                </div>
                                            </div>
                                            <div className="rtx-table-scroll" style={{maxHeight: 'none'}}>
                                                <table className="rtx-table">
                                                    <thead>
                                                    <tr>
                                                        <th style={{width: '90px'}}>위치</th>
                                                        <th className="rtx-left" style={{width: '300px'}}>항목</th>
                                                        <th className="rtx-num" style={{width: '160px'}}>삽입값</th>
                                                        <th className="rtx-left">계산근거</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <tr>
                                                        <td>F11</td>
                                                        <td className="rtx-left">구매대행 해외배송분 구매비(수출)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF11)}</td>
                                                        <td className="rtx-left">
                                                            구매카드내역 {formatWon(finalGenLog.orderCardTotal)} − F13 {formatWon(finalGenLog.valF13)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>F12</td>
                                                        <td className="rtx-left">해외배송수수료(수출)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF12)}</td>
                                                        <td className="rtx-left" style={{whiteSpace: 'normal', lineHeight: '20px'}}>
                                                            선편차익 {formatNum(finalGenLog.seaProfit)} + K-Packet차익 {formatNum(finalGenLog.kPackProfit)}
                                                            + S-Packet차익 {formatNum(finalGenLog.sPackProfit)}
                                                            + 해외배송수수료 {formatNum(finalGenLog.overseasFeeTotal)}
                                                            + FedEx차익 {formatNum(finalGenLog.fedexProfit)}
                                                            + UPS차익 {formatNum(finalGenLog.upsProfit)}
                                                            + 오프라인 {formatNum(finalGenLog.offlineFee)}
                                                            + 아넬 {formatNum(finalGenLog.anellFee)}
                                                            <br/><span style={{color: '#9a9a9a'}}>
                                                                ※ FedEx차익 = (배송료+기타 {formatNum(vatAgg.ttFedexPrice)}) − 청구서 BN열 전체 {formatNum(finalGenLog.fedexBillTotal)}
                                                                &nbsp;/&nbsp; UPS차익 = (배송료+기타 {formatNum(vatAgg.ttUpsPrice)}) − 청구서 BA열 전체 {formatNum(finalGenLog.upsBillTotal)}
                                                                <br/>※ 청구서 총액은 운송/관세·세금 구분없이, DB 미매칭(청구오류) 운송장까지 포함한 전 표시행 합계입니다.
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>F13</td>
                                                        <td className="rtx-left">구매대행 국내발송용 상품 구매비(과세)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF13)}</td>
                                                        <td className="rtx-left">구매일지 국내배송 실결제(C열, 전체취소 제외)</td>
                                                    </tr>
                                                    <tr>
                                                        <td>F14</td>
                                                        <td className="rtx-left">구매대행 국내배송분 수수료(과세)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF14)}</td>
                                                        <td className="rtx-left">
                                                            택배비수익 {formatNum(vatAgg.domesticFormShipFee)} + 5종수수료 {formatNum(vatAgg.domesticFormFee.total)}
                                                            &nbsp;+ 구매일지 국내배송 수수료 {formatNum(finalGenLog.logDomesticFee)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>F15</td>
                                                        <td className="rtx-left">배송대행 국내배송분 수수료(과세)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF15)}</td>
                                                        <td className="rtx-left">
                                                            택배비수익 {formatNum(vatAgg.deliveryFormShipFee)} + 5종수수료 {formatNum(vatAgg.deliveryFormFee.total)}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>F16</td>
                                                        <td className="rtx-left">결제대행 수수료(과세)</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.valF16)}</td>
                                                        <td className="rtx-left">구매일지 이체대행 7% 수수료 총합</td>
                                                    </tr>
                                                    <tr>
                                                        <td>D24</td>
                                                        <td className="rtx-left">우체국 페이백 총금액</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.paybackTotal)}</td>
                                                        <td className="rtx-left">
                                                            비용카드내역 우정사업본부 · 음수 · 유일금액 {formatNum(finalGenLog.paybackRowCount)}건 (탭2 목록과 동일)
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>탭8</td>
                                                        <td className="rtx-left">적립금사용내역 합계</td>
                                                        <td className="rtx-num">{formatNum(finalGenLog.pointTotal)}</td>
                                                        <td className="rtx-left">
                                                            {formatNum(finalGenLog.pointCount)}건 (취소=금액앞 '-' / 사용=금액 그대로)
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="rtx-formula-box" style={{color: '#9a9a9a'}}>
                                                ▶ 공란처리 : F28:G36 · G37 · E43:E53 · G43:G57 &nbsp;/&nbsp;
                                                그 외 탭1의 모든 셀·서식·병합은 원본 양식 그대로 유지됩니다.
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {!vatPt1All.length && !vatLoading && !vatErrorMsg && (
                                <div className="rtx-empty-box">
                                    조회기간을 설정하고 [데이터조회] 버튼을 누르면 세무 데이터 필터링 팝업이 열립니다.
                                </div>
                            )}

                            {/* 세무 데이터 필터링 팝업 */}
                            <AdminRenewTaxFilterModal
                                show={vatFilterShow}
                                pt1Data={vatPt1All}
                                closePopup={() => setVatFilterShow(false)}
                                completeFilter={handleCompleteVatFilter}
                            />
                        </div>
                    )}

                    {/* ================= 법인세 탭 (추후 구현) ================= */}
                    {mainTab === 'CT' && (
                        <div className="rtx-empty-box">법인세 신고데이터 생성 기능은 추후 구현 예정입니다.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminRenewTaxPg;
